// src/element.tsx
import { render } from "preact";

// src/app.tsx
import { useEffect as useEffect3, useRef as useRef2, useState as useState3 } from "preact/hooks";
import {
  activeQuantityTier as activeQuantityTier2,
  applyTextTransform,
  canonicalDeclaredMime as canonicalDeclaredMime2,
  checkboxConfigSchema,
  dateConfigSchema,
  initialSelection,
  isMimeAllowed,
  isSizedUploadOptionType as isSizedUploadOptionType2,
  monotonicProgress,
  numberConfigSchema,
  textConfigSchema,
  uploadAcceptAttribute as uploadAcceptAttribute2,
  uploadProgressPercent
} from "@tkpod/shared/product-options";

// src/api-error.ts
var OptionsRequestError = class extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
    this.name = "OptionsRequestError";
  }
};
function mapOptionsError(err, options) {
  const code = err instanceof OptionsRequestError ? err.code : null;
  const message = err instanceof Error && err.message ? err.message : "Could not add this product.";
  if (code === "OPTIONS_VARIANT_REQUIRED") {
    return { form: "Choose a variant before continuing.", fields: {} };
  }
  if (code === "OPTIONS_UPLOAD_NOT_READY") {
    return { form: "Wait until each file is ready.", fields: {} };
  }
  if (code === "OPTIONS_BLOCK_DISABLED") {
    return { form: "Product options are turned off.", fields: {} };
  }
  if (code === "OPTIONS_QUOTA_EXCEEDED") {
    return { form: message, fields: {} };
  }
  if (code === "OPTIONS_SELECTION_INVALID") {
    if (message.startsWith("Quantity ")) return { form: message, fields: {} };
    for (const option of options) {
      const prefix = `${option.displayName}: `;
      if (message.startsWith(prefix)) {
        return { form: null, fields: { [option.key]: message.slice(prefix.length) } };
      }
    }
  }
  return { form: message, fields: {} };
}

// src/cart.ts
function cartPropertiesObject(properties) {
  const result = {};
  for (const property of properties) {
    if (property.name.trim() === "") continue;
    result[property.name] = property.value;
  }
  return result;
}
var MAX_SECTIONS_PER_REQUEST = 5;
var WELL_KNOWN_SECTIONS = [
  { name: "cart-drawer", selector: "#CartDrawer" },
  { name: "cart-icon-bubble", selector: "#cart-icon-bubble" },
  { name: "cart-notification", selector: "#cart-notification" }
];
function collectCartSectionNames(doc = document) {
  const names = /* @__PURE__ */ new Set();
  try {
    const nodes = doc.querySelectorAll('[id^="shopify-section-"]');
    for (let i = 0; i < nodes.length; i++) {
      const name = nodes[i].id.slice("shopify-section-".length);
      if (name && /cart/i.test(name)) names.add(name);
    }
  } catch {
  }
  for (const entry of WELL_KNOWN_SECTIONS) names.add(entry.name);
  return [...names].slice(0, MAX_SECTIONS_PER_REQUEST);
}
function applyRenderedSections(sections, doc = document) {
  let applied = 0;
  for (const name of Object.keys(sections)) {
    const html = sections[name];
    if (typeof html !== "string" || !html) continue;
    try {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const wrapper = doc.getElementById(`shopify-section-${name}`);
      if (wrapper) {
        const source2 = parsed.querySelector(".shopify-section") ?? parsed.getElementById(`shopify-section-${name}`) ?? parsed.body;
        wrapper.innerHTML = source2.innerHTML;
        applied++;
        continue;
      }
      const known = WELL_KNOWN_SECTIONS.find((entry) => entry.name === name);
      if (!known) continue;
      const target = doc.querySelector(known.selector);
      const source = parsed.querySelector(known.selector) ?? parsed.querySelector(".shopify-section") ?? parsed.body;
      if (target && source) {
        target.innerHTML = source.innerHTML;
        applied++;
      }
    } catch {
    }
  }
  return applied;
}
function openThemeCart(doc) {
  const nodes = doc.querySelectorAll("cart-drawer, cart-notification, cart-drawer-component");
  nodes.forEach((node) => {
    const opener = node;
    try {
      if (typeof opener.open === "function") opener.open();
      else if (typeof opener.show === "function") opener.show();
    } catch {
    }
  });
}
function notifyCartUpdated(cart, sectionsRefreshed, doc) {
  const detail = { cart, source: "tk-product-options", sectionsRefreshed };
  doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail }));
  doc.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail }));
  doc.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "tk-product-options",
        data: { source: "tk-product-options", sectionsRefreshed }
      }
    })
  );
}
function shopifyUpdateCart() {
  const host = globalThis;
  const updateCart = host.Shopify?.actions?.updateCart;
  return typeof updateCart === "function" ? updateCart : null;
}
function cartErrorMessage(body, status) {
  if (typeof body.description === "string" && body.description) return body.description;
  if (typeof body.message === "string" && body.message) return body.message;
  return `Could not add to cart (${status}).`;
}
async function addShopifyCartLine(input, fetchImpl = fetch, doc = document) {
  const updateCart = shopifyUpdateCart();
  if (updateCart) {
    const result = await updateCart(
      {
        lines: [
          {
            merchandiseId: input.variantId,
            quantity: input.quantity,
            attributes: input.properties.filter((property) => property.name.trim() !== "").map((property) => ({ key: property.name, value: property.value }))
          }
        ]
      },
      { event: { context: "product" } }
    );
    const message = result?.userErrors?.find((error) => error.message)?.message;
    if (message) throw new Error(message);
    return;
  }
  const sectionNames = collectCartSectionNames(doc);
  const variantId = /^\d+$/.test(input.variantId) ? Number(input.variantId) : input.variantId;
  const response = await fetchImpl("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: input.quantity,
          properties: cartPropertiesObject(input.properties)
        }
      ],
      ...sectionNames.length > 0 ? { sections: sectionNames.join(","), sections_url: doc.location?.pathname || "/" } : {}
    })
  });
  let body = {};
  try {
    body = await response.json();
  } catch {
    body = {};
  }
  const reportedStatus = typeof body.status === "number" ? body.status : null;
  if (!response.ok || reportedStatus != null && reportedStatus >= 400) {
    throw new Error(cartErrorMessage(body, response.status));
  }
  if (body.sections) applyRenderedSections(body.sections, doc);
  openThemeCart(doc);
  let cart = null;
  try {
    const cartResponse = await fetchImpl("/cart.js", { headers: { Accept: "application/json" } });
    if (cartResponse.ok) cart = await cartResponse.json();
  } catch {
    cart = null;
  }
  notifyCartUpdated(cart, body.sections ? Object.keys(body.sections).length : 0, doc);
}
var ADD_TO_ORDER_EVENT = "tk:add-to-order";
function dispatchAddToOrder(target, detail) {
  target.dispatchEvent(
    new CustomEvent(ADD_TO_ORDER_EVENT, {
      bubbles: true,
      composed: true,
      detail
    })
  );
}

// src/money.ts
var AMOUNT_TOKENS = [
  "amount_no_decimals_with_comma_separator",
  "amount_with_comma_separator",
  "amount_no_decimals_with_space_separator",
  "amount_with_space_separator",
  "amount_with_apostrophe_separator",
  "amount_no_decimals",
  "amount"
];
function group(value, separator) {
  const [whole, fraction] = value.split(".");
  const grouped = (whole ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return fraction !== void 0 ? `${grouped}.${fraction}` : grouped;
}
function readPresentmentCurrency(host = globalThis) {
  const currency = host.Shopify?.currency;
  const active = typeof currency?.active === "string" ? currency.active.trim() : "";
  const rate = typeof currency?.rate === "string" ? Number(currency.rate) : currency?.rate;
  if (active === "" || typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) return null;
  return { active, rate };
}
function formatMoney(cents, format = "${{amount}}") {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const major = (abs / 100).toFixed(2);
  const noDecimals = String(Math.round(abs / 100));
  const comma = major.replace(".", ",");
  const values = {
    amount: group(major, ","),
    amount_no_decimals: group(noDecimals, ","),
    amount_with_comma_separator: group(comma, "."),
    amount_no_decimals_with_comma_separator: group(noDecimals, "."),
    amount_with_space_separator: group(comma, " "),
    amount_no_decimals_with_space_separator: group(noDecimals, " "),
    amount_with_apostrophe_separator: group(major, "'")
  };
  let rendered = format;
  for (const token of AMOUNT_TOKENS) {
    rendered = rendered.split(`{{${token}}}`).join(values[token]);
  }
  return negative ? `-${rendered}` : rendered;
}

// src/model.ts
import {
  customSizeConfigSchema,
  displayRulesSchema,
  getOptionType,
  isFilled,
  isSizedUploadOptionType,
  matchVariantForSize,
  visibleInOrder,
  normalizeChoices,
  optionsBlockConfigSchema,
  productOptionChoiceSchema,
  resolveOptionPrice,
  sizeFormulaMode,
  SIZED_STRETCH_FLAG,
  uploadConfigSchema
} from "@tkpod/shared/product-options";
var SUPPORTED = /* @__PURE__ */ new Set([
  "TEXT",
  "TEXTAREA",
  "STRING",
  "NUMBER",
  "CHECKBOX",
  "CHECKBOX_GROUP",
  "BOOLEAN",
  "RADIO",
  "SELECT",
  "SWATCH",
  "BUTTON_GROUP",
  "DATE",
  "CUSTOM_SIZE",
  "INFO",
  "FILE_UPLOAD",
  "IMAGE_UPLOAD_SIZED",
  "GANGSHEET_UPLOAD_SIZED"
]);
var UPLOAD_TYPES = /* @__PURE__ */ new Set(["FILE_UPLOAD", "IMAGE_UPLOAD_SIZED", "GANGSHEET_UPLOAD_SIZED"]);
function isSupportedOption(optionType) {
  return SUPPORTED.has(optionType);
}
function isUploadOption(optionType) {
  return UPLOAD_TYPES.has(optionType);
}
function parseBlock(config) {
  const parsed = optionsBlockConfigSchema.safeParse(config ?? {});
  return parsed.success ? parsed.data : optionsBlockConfigSchema.parse({});
}
function parseChoices(values, optionType = "") {
  const rows = Array.isArray(values) ? values : [];
  const kept = [];
  for (const row of rows) {
    const parsed = productOptionChoiceSchema.safeParse(row);
    if (parsed.success) kept.push(parsed.data);
  }
  return normalizeChoices(optionType, kept);
}
function visibleOptions(options, context) {
  return visibleInOrder(
    options.filter((option) => option.role !== "VARIANT_SELECTOR"),
    context,
    (option) => {
      if (option.displayRules == null) return [];
      const rules = displayRulesSchema.safeParse(option.displayRules);
      return rules.success ? rules.data : null;
    }
  );
}
function pricesSelectedBlank(option, blankVariantId) {
  if (!blankVariantId || option.optionType !== "CUSTOM_SIZE") return false;
  const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
  return parsed.success && parsed.data.pricingVariantId === blankVariantId;
}
function fieldError(option, field, options) {
  if (!isSupportedOption(option.optionType)) {
    return "This option is not available yet.";
  }
  if (option.optionType === "INFO") return null;
  const plugin = getOptionType(option.optionType);
  if (!plugin) return "This option is misconfigured.";
  const config = plugin.configSchema.safeParse(option.config ?? {});
  if (!config.success) return "This option is misconfigured.";
  const selection = field ? { optionKey: option.key, values: field.values, uploadTokens: field.uploadTokens } : null;
  const error = plugin.validate(
    selection,
    config.data,
    option.isRequired || options?.requireFilled === true
  );
  if (error) return error;
  if (plugin.capabilities.usesChoices && field) {
    const allowed = new Set(parseChoices(option.values, option.optionType).map((choice) => choice.key));
    if (field.values.some((value) => value.trim() !== "" && !allowed.has(value))) {
      return "Choose one of the available options.";
    }
  }
  return null;
}
function pricingFor(options, fields, variantUnitPriceCents, quantity, context) {
  const pricing = isQuantityConfig(context) ? { quantity: context } : context ?? {};
  const quantityConfig = pricing.quantity;
  const scale = presentmentScale(pricing.presentmentRate);
  const formulaRateCents = pricing.formulaRateCents == null ? null : scale(pricing.formulaRateCents);
  const pricingOptions = [];
  const selections = {};
  for (const option of options) {
    if (option.optionType === "INFO") continue;
    const field = fields[option.key];
    if (!field || !isFilled(field)) continue;
    const choices = parseChoices(option.values, option.optionType);
    const choiceDeltas = {};
    for (const choice of choices) {
      if (typeof choice.priceDeltaCents === "number") choiceDeltas[choice.key] = choice.priceDeltaCents;
    }
    pricingOptions.push({
      key: option.key,
      label: option.lineItemLabel?.trim() || option.displayName,
      priceDeltaMode: option.priceDeltaMode,
      priceDeltaCents: option.priceDeltaCents == null ? null : scale(option.priceDeltaCents),
      priceDeltaPercent: asNumber(option.priceDeltaPercent),
      choiceDeltas: scaleRecord(choiceDeltas, scale)
    });
    selections[option.key] = field.values;
  }
  let formula = null;
  if (pricing.formula) {
    formula = {
      width: pricing.formula.width,
      height: pricing.formula.height,
      rateCents: scale(pricing.formula.rateCents),
      minChargeCents: scale(pricing.minChargeCents ?? 0),
      mode: pricing.formula.mode
    };
  } else if (formulaRateCents != null) {
    for (const option of options) {
      if (option.optionType !== "CUSTOM_SIZE") continue;
      const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
      if (!parsed.success || !parsed.data.pricingVariantId) continue;
      const field = fields[option.key];
      const width = Number(field?.values[0]);
      const height = Number(field?.values[1]);
      if (!Number.isFinite(width) || !Number.isFinite(height)) continue;
      formula = {
        width,
        height,
        rateCents: formulaRateCents,
        minChargeCents: scale(pricing.minChargeCents ?? 0)
      };
      break;
    }
  }
  return resolveOptionPrice({
    variantUnitPriceCents: scale(variantUnitPriceCents),
    quantity,
    options: pricingOptions,
    selections,
    formula,
    tiers: quantityConfig?.mode === "BULK" ? quantityConfig.tiers : void 0,
    tierQuantity: pricing.tierQuantity
  });
}
function isQuantityConfig(value) {
  return value != null && typeof value === "object" && "mode" in value && "min" in value;
}
function presentmentScale(rate) {
  if (rate == null || !Number.isFinite(rate) || rate <= 0 || rate === 1) return (cents) => cents;
  return (cents) => Math.round(cents * rate);
}
function scaleRecord(deltas, scale) {
  const scaled = {};
  for (const key of Object.keys(deltas)) scaled[key] = scale(deltas[key]);
  return scaled;
}
function asNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}
function uploadLimits(option) {
  const parsed = uploadConfigSchema.safeParse(option.config ?? {});
  if (!parsed.success) return null;
  return parsed.data.accept;
}
function quantityError(quantity, bounds) {
  if (quantity < bounds.min || quantity > bounds.max) {
    return `Quantity must be between ${bounds.min} and ${bounds.max}.`;
  }
  if ((quantity - bounds.min) % bounds.step !== 0) {
    return `Quantity must increase in steps of ${bounds.step}.`;
  }
  return null;
}
function cartPricingProperties(options, fields, mode) {
  const custom = options.find((option) => {
    if (option.optionType !== "CUSTOM_SIZE") return false;
    const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
    return parsed.success && Boolean(parsed.data.pricingVariantId);
  });
  const sizedUpload = options.find((option) => isSizedUploadOptionType(option.optionType));
  const source = custom ?? sizedUpload;
  const width = source ? fields[source.key]?.values[0]?.trim() ?? "" : "";
  const height = source ? fields[source.key]?.values[1]?.trim() ?? "" : "";
  const hasSize = width !== "" && height !== "";
  const selected = pricedSelectionAttribute(options, fields);
  if (!hasSize && mode !== "BULK" && selected === "") return [];
  const properties = [{ name: "_tk_qty_mode", value: mode }];
  if (hasSize) {
    properties.push({ name: "_tk_w", value: width }, { name: "_tk_h", value: height });
  }
  if (selected !== "") properties.push({ name: "_tk_sel", value: selected });
  return properties;
}
function pricedSelectionAttribute(options, fields) {
  const parts = [];
  for (const option of options) {
    if (option.optionType === "INFO") continue;
    const field = fields[option.key];
    if (!field || !isFilled(field)) continue;
    const choices = parseChoices(option.values, option.optionType);
    const hasChoicePrice = choices.some((choice) => typeof choice.priceDeltaCents === "number");
    if (option.priceDeltaMode === "NONE" && !hasChoicePrice) continue;
    const values = field.values.map((value) => value.trim()).filter((value) => value !== "");
    parts.push(values.length > 0 ? `${option.key}:${values.join("+")}` : option.key);
  }
  return parts.join("|");
}
function submissionSelections(options, fields) {
  return options.filter((option) => option.optionType !== "INFO").map((option) => {
    const field = fields[option.key] ?? { values: [], uploadTokens: [] };
    return {
      optionKey: option.key,
      values: field.values,
      uploadTokens: field.uploadTokens
    };
  });
}
function sizedSelectionValues(item) {
  const values = [String(item.widthIn), String(item.heightIn), item.presetId || "detected"];
  if (item.stretched) values.push(SIZED_STRETCH_FLAG);
  return values;
}
var BAND_RULES = [
  "LONGEST_EDGE_CEIL",
  "HEIGHT_CEIL",
  "WIDTH_CEIL",
  "AREA_CEIL",
  "BOTH_EDGES_CEIL"
];
function bandRule(value) {
  if (value && BAND_RULES.includes(value)) return value;
  return "LONGEST_EDGE_CEIL";
}
function rateBlank(pricingVariantId, variants) {
  if (!pricingVariantId) return null;
  return variants.find((variant) => variant.blankVariantId === pricingVariantId) ?? null;
}
function matchBand(optionType, config, variants, widthIn, heightIn) {
  const gang = optionType === "GANGSHEET_UPLOAD_SIZED";
  const fixed = config.variantBinding?.fixedWidthIn;
  const sheetWidth = typeof fixed === "number" && fixed > 0 ? fixed : 22;
  const ordered = gang ? [...variants].sort(
    (a, b) => (a.priceCents ?? 0) - (b.priceCents ?? 0) || a.productVariantId.localeCompare(b.productVariantId)
  ) : variants;
  const candidates = ordered.flatMap((variant) => {
    if (gang) {
      const height = variant.heightIn != null && variant.heightIn > 0 ? variant.heightIn : variant.widthIn != null && variant.widthIn > 0 ? variant.widthIn : null;
      if (height == null) return [];
      return [{ variantId: variant.productVariantId, widthIn: sheetWidth, heightIn: height }];
    }
    if (variant.widthIn == null || variant.heightIn == null) return [];
    if (variant.widthIn <= 0 || variant.heightIn <= 0) return [];
    return [{ variantId: variant.productVariantId, widthIn: variant.widthIn, heightIn: variant.heightIn }];
  });
  const match = matchVariantForSize({
    rule: gang ? "HEIGHT_CEIL" : bandRule(config.variantBinding?.defaultVariantRule),
    detectedWidthIn: widthIn,
    detectedHeightIn: heightIn,
    allowSmallerThanDetected: config.variantBinding?.allowSmallerThanDetected === true,
    candidates
  });
  if (!match) return null;
  return variants.find((variant) => variant.productVariantId === match.variantId) ?? null;
}
function quoteSizedUploads(input) {
  if (!isSizedUploadOptionType(input.option.optionType)) return null;
  if (input.items.length === 0) return null;
  const parsed = uploadConfigSchema.safeParse(input.option.config ?? {});
  if (!parsed.success) return { ok: false, error: "This option is misconfigured." };
  const mode = parsed.data.sizePricing?.mode ?? "AREA_RATE";
  const tierQuantity = input.items.reduce((sum, item) => sum + Math.max(1, item.quantity), 0);
  const lines = [];
  for (const item of input.items) {
    const field = {
      values: sizedSelectionValues(item),
      uploadTokens: [item.token]
    };
    const fields = { ...input.fields, [input.option.key]: field };
    let variant = input.fallbackVariant;
    let formula = null;
    switch (mode) {
      case "VARIANT_BAND": {
        const matched = matchBand(input.option.optionType, parsed.data, input.variants, item.widthIn, item.heightIn);
        if (!matched || matched.priceCents == null) {
          return { ok: false, error: matched ? "Price unavailable" : "No size covers this image." };
        }
        variant = matched;
        break;
      }
      case "AREA_RATE":
      case "LONGEST_EDGE_RATE":
      case "HEIGHT_RATE": {
        const rate = rateBlank(parsed.data.sizePricing?.pricingVariantId, input.variants)?.priceCents;
        const formulaMode = sizeFormulaMode(mode);
        if (rate == null || formulaMode == null) return { ok: false, error: "Price unavailable" };
        formula = {
          width: item.widthIn,
          height: item.heightIn,
          rateCents: rate,
          mode: formulaMode
        };
        break;
      }
      default: {
        const unreachable = mode;
        return unreachable;
      }
    }
    const priced = pricingFor(input.options, fields, variant?.priceCents ?? 0, item.quantity, {
      ...input.context,
      tierQuantity,
      formula
    });
    lines.push({
      id: item.id,
      token: item.token,
      quantity: item.quantity,
      unitCents: priced.customerUnitCents,
      listUnitCents: priced.customerUnitCents + priced.discountCents,
      discountPercent: priced.discountPercent,
      lineTotalCents: priced.lineTotalCents,
      variant
    });
  }
  return {
    ok: true,
    lines,
    lineTotalCents: lines.reduce((sum, line) => sum + line.lineTotalCents, 0)
  };
}

// src/draft-sync.ts
var timers = /* @__PURE__ */ new Map();
function scheduleDraftSave(transport, card) {
  if (!card.token || card.status !== "ready") return;
  const widthIn = card.widthIn;
  const heightIn = card.heightIn;
  const sized = widthIn != null && heightIn != null && widthIn > 0 && heightIn > 0;
  const token = card.token;
  const pending = timers.get(token);
  if (pending) clearTimeout(pending);
  const hex = card.previewBg?.trim() ?? "";
  const quantity = card.quantity && card.quantity > 0 ? Math.round(card.quantity) : 1;
  const draft = sized ? {
    schemaVersion: 1,
    widthIn,
    heightIn,
    presetId: card.presetId && card.presetId.length > 0 ? card.presetId : "custom",
    quantity,
    bgColorHex: /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex.toUpperCase() : null,
    ratioLocked: card.ratioLocked !== false
  } : { schemaVersion: 1, quantity, ratioLocked: true };
  timers.set(token, setTimeout(() => {
    timers.delete(token);
    void transport.saveDraft(token, draft).catch(() => void 0);
  }, 800));
}

// src/upload-client.ts
import { canonicalDeclaredMime, optionUploadFailureMessage } from "@tkpod/shared/product-options";

// src/session.ts
var SESSION_KEY = "tkpod_designer_session";
var CUSTOMER_KEY = "tkpod_customer_token_v1";
var UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
var memorySession = null;
function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = bytes[6] & 15 | 64;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function getDesignerSessionId() {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing && UUID_V4.test(existing)) return existing;
    const token = uuid();
    localStorage.setItem(SESSION_KEY, token);
    return token;
  } catch {
    if (!memorySession) memorySession = uuid();
    return memorySession;
  }
}
function getCustomerToken() {
  try {
    const raw = sessionStorage.getItem(CUSTOMER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.v !== "string" || typeof parsed.e !== "number") return null;
    if (parsed.e - Date.now() < 6e4) return null;
    return parsed.v;
  } catch {
    return null;
  }
}
function idempotencyKey() {
  return uuid();
}

// src/upload-client.ts
var RESUME_KEY = "tkpod_option_upload_v1";
var PART_CONCURRENCY = 3;
var PART_ATTEMPTS = 3;
function fingerprint(file, uptId, optionKey) {
  return `${uptId}:${optionKey}:${file.name}:${file.size}:${file.lastModified}`;
}
function readResume(storage) {
  try {
    const parsed = JSON.parse(storage.getItem(RESUME_KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
function writeResume(storage, map) {
  const entries = Object.entries(map).slice(-20);
  storage.setItem(RESUME_KEY, JSON.stringify(Object.fromEntries(entries)));
}
async function mapPool(items, limit, run) {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      const item = items[index];
      if (item !== void 0) await run(item);
    }
  });
  await Promise.all(workers);
}
var PartHttpError = class extends Error {
  constructor(status) {
    super(`Upload part failed (${status}).`);
    this.status = status;
  }
};
function linkedSignal(signal, extra) {
  if (!signal) return extra;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal.aborted || extra.aborted) {
    controller.abort();
    return controller.signal;
  }
  signal.addEventListener("abort", abort, { once: true });
  extra.addEventListener("abort", abort, { once: true });
  return controller.signal;
}
async function putPart(fetchImpl, url, body, signal) {
  let lastError;
  for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetchImpl(url, {
        method: "PUT",
        body,
        signal,
        // A sliced File has an empty type. Without this header the browser
        // omits Content-Type and the local API rejects the part with 415.
        // S3 part URLs are not content-type signed, so the header is safe there.
        headers: { "Content-Type": body.type || "application/octet-stream" }
      });
      if (!response.ok) throw new PartHttpError(response.status ?? 0);
      const header = response.headers.get("etag");
      if (header && header.trim() !== "") return header.trim();
      const json = await response.clone().json().catch(() => null);
      if (json && typeof json.etag === "string" && json.etag.trim() !== "") return json.etag.trim();
      throw new Error("The upload part did not return an ETag.");
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
      if (attempt < PART_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Upload part failed.");
}
async function uploadOptionFile(input) {
  const fetchImpl = input.fetchImpl ?? ((url, init) => fetch(url, init));
  const storage = input.storage ?? localStorage;
  const key = fingerprint(input.file, input.uptId, input.optionKey);
  const map = readResume(storage);
  let record = map[key];
  const persist = () => {
    map[key] = record;
    writeResume(storage, map);
  };
  if (record) {
    try {
      const status = await input.transport.uploadStatus(record.token);
      if (status.status === "READY" || status.status === "PROCESSING") return status;
      if (status.status === "FAILED") record = void 0;
    } catch {
      record = void 0;
    }
  }
  let created;
  if (!record) {
    const keyForAttempt = idempotencyKey();
    created = await input.transport.initUpload(
      {
        uptId: input.uptId,
        optionKey: input.optionKey,
        fileName: input.file.name,
        mimeType: canonicalDeclaredMime(input.file.type, input.file.name) || "application/octet-stream",
        sizeBytes: input.file.size
      },
      keyForAttempt
    );
    record = {
      token: created.token,
      idempotencyKey: keyForAttempt,
      partSizeBytes: created.partSizeBytes,
      etags: {},
      uptId: input.uptId,
      optionKey: input.optionKey,
      size: input.file.size
    };
    persist();
    if (created.status === "READY" || created.status === "PROCESSING") {
      return {
        token: created.token,
        status: created.status,
        scanStatus: created.scanStatus
      };
    }
  }
  const current = record;
  const partSize = current.partSizeBytes;
  const partCount = Math.max(1, Math.ceil(input.file.size / partSize));
  const pending = [];
  for (let part = 1; part <= partCount; part += 1) {
    if (!current.etags[String(part)]) pending.push(part);
  }
  const poolAbort = new AbortController();
  const signal = linkedSignal(input.signal, poolAbort.signal);
  const slicePart = (partNumber) => input.file.slice(
    (partNumber - 1) * partSize,
    Math.min(partNumber * partSize, input.file.size),
    input.file.type || "application/octet-stream"
  );
  try {
    for (let offset = 0; offset < pending.length; offset += 50) {
      const batch = pending.slice(offset, offset + 50);
      const signed = await input.transport.signParts(current.token, batch);
      const byNumber = new Map(signed.parts.map((part) => [part.partNumber, part.url]));
      await mapPool(batch, PART_CONCURRENCY, async (partNumber) => {
        let url = byNumber.get(partNumber);
        if (!url) throw new Error("A signed upload URL is missing.");
        let etag;
        try {
          etag = await putPart(fetchImpl, url, slicePart(partNumber), signal);
        } catch (err) {
          if (!(err instanceof PartHttpError) || err.status < 400 || err.status >= 500) throw err;
          const resigned = await input.transport.signParts(current.token, [partNumber]);
          url = resigned.parts[0]?.url;
          if (!url) throw err;
          etag = await putPart(fetchImpl, url, slicePart(partNumber), signal);
        }
        current.etags[String(partNumber)] = etag;
        persist();
        input.onProgress?.({
          loaded: Object.keys(current.etags).length * partSize,
          total: input.file.size
        });
      });
    }
  } catch (err) {
    poolAbort.abort();
    throw err;
  }
  const parts = [];
  for (let part = 1; part <= partCount; part += 1) {
    const etag = current.etags[String(part)];
    if (!etag) throw new Error("An upload part is missing.");
    parts.push({ partNumber: part, etag });
  }
  const completed = await input.transport.completeUpload(current.token, parts);
  delete map[key];
  writeResume(storage, map);
  return completed;
}
async function pollUploadReady(transport, token, options) {
  const intervalMs = options?.intervalMs ?? 1500;
  const timeoutMs = options?.timeoutMs ?? 5 * 6e4;
  const started = Date.now();
  let latest = await transport.uploadStatus(token);
  while (latest.status === "PROCESSING" || latest.status === "UPLOADING") {
    if (Date.now() - started > timeoutMs) {
      throw new Error("The file is still being checked. Try again in a moment.");
    }
    await new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, intervalMs);
      options?.signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          reject(new Error("Upload cancelled."));
        },
        { once: true }
      );
    });
    latest = await transport.uploadStatus(token);
  }
  if (latest.status === "FAILED") {
    throw new Error(
      optionUploadFailureMessage(latest.failureCode, options?.limits) ?? "The file could not be accepted."
    );
  }
  return latest;
}

// src/renderers.tsx
import { useEffect, useRef, useState } from "preact/hooks";
import {
  checkboxGroupConfigSchema,
  customSizeConfigSchema as customSizeConfigSchema2,
  swatchSizePx,
  uploadAcceptAttribute
} from "@tkpod/shared/product-options";
import { jsx, jsxs } from "preact/jsx-runtime";
function FieldFrame(props) {
  return /* @__PURE__ */ jsxs("div", { class: "field", children: [
    props.option.optionType !== "INFO" ? /* @__PURE__ */ jsxs("label", { htmlFor: `tk-opt-${props.option.key}`, children: [
      props.option.displayName,
      props.selected ? `: ${props.selected}` : null,
      props.option.isRequired ? /* @__PURE__ */ jsx("span", { class: "req", children: " *" }) : null
    ] }) : null,
    props.children,
    props.option.helpText ? /* @__PURE__ */ jsx("p", { class: "help", children: props.option.helpText }) : null,
    props.error ? /* @__PURE__ */ jsx("p", { class: "error", id: `tk-opt-${props.option.key}-error`, role: "alert", children: props.error }) : null
  ] });
}
function ChoiceControl(props) {
  const choices = parseChoices(props.option.values, props.option.optionType);
  if (props.option.optionType === "SELECT") {
    return /* @__PURE__ */ jsxs(
      "select",
      {
        value: props.value,
        onChange: (event) => props.onChange(event.currentTarget.value),
        children: [
          /* @__PURE__ */ jsx("option", { value: "", children: props.option.placeholder || "Select" }),
          choices.map((choice) => /* @__PURE__ */ jsx("option", { value: choice.key, children: choice.label }, choice.key))
        ]
      }
    );
  }
  if (props.option.optionType === "SWATCH") {
    const px = swatchSizePx(props.option.config);
    return /* @__PURE__ */ jsx(
      "div",
      {
        class: "choices",
        role: "group",
        "aria-label": props.option.displayName,
        style: { "--tk-swatch": `${px}px` },
        children: choices.map((choice) => /* @__PURE__ */ jsx(Swatch, { choice, pressed: props.value === choice.key, onChange: props.onChange }, choice.key))
      }
    );
  }
  if (props.option.optionType === "BUTTON_GROUP") {
    const showImages2 = choices.some((choice) => Boolean(choice.imageUrl));
    return /* @__PURE__ */ jsx("div", { class: showImages2 ? "choices cards" : "choices", role: "group", "aria-label": props.option.displayName, children: choices.map((choice) => /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        class: showImages2 ? "choice card" : "choice",
        "aria-pressed": props.value === choice.key,
        onClick: () => props.onChange(choice.key),
        children: [
          showImages2 && choice.imageUrl ? /* @__PURE__ */ jsx("img", { src: choice.imageUrl, alt: "" }) : null,
          /* @__PURE__ */ jsx("span", { children: choice.label })
        ]
      },
      choice.key
    )) });
  }
  const showImages = choices.some((choice) => Boolean(choice.imageUrl));
  return /* @__PURE__ */ jsx("div", { class: showImages ? "choices cards" : "choices", role: "radiogroup", "aria-label": props.option.displayName, children: choices.map((choice) => /* @__PURE__ */ jsxs("label", { class: showImages ? "choice card" : "choice", children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "radio",
        name: props.option.key,
        checked: props.value === choice.key,
        onChange: () => props.onChange(choice.key)
      }
    ),
    showImages && choice.imageUrl ? /* @__PURE__ */ jsx("img", { src: choice.imageUrl, alt: "" }) : null,
    /* @__PURE__ */ jsx("span", { children: choice.label })
  ] }, choice.key)) });
}
function safeBackground(url) {
  if (!url || !/^(\/|https?:\/\/)/.test(url) || /["'()]/.test(url)) return void 0;
  return { backgroundImage: `url("${url}")`, backgroundSize: "cover" };
}
function Swatch(props) {
  const image = props.choice.imageUrl;
  const style = !image && props.choice.colorHex ? { background: props.choice.colorHex } : void 0;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      class: "swatch",
      style,
      title: props.choice.label,
      "aria-label": props.choice.label,
      "aria-pressed": props.pressed,
      onClick: () => props.onChange(props.choice.key),
      children: [
        image ? /* @__PURE__ */ jsx("img", { src: image, alt: "" }) : null,
        props.pressed ? /* @__PURE__ */ jsx("span", { class: "swatch-mark", "aria-hidden": "true", children: "\u2713" }) : null
      ]
    }
  );
}
function CheckboxGroup(props) {
  const choices = parseChoices(props.option.values, props.option.optionType);
  const showImages = choices.some((choice) => Boolean(choice.imageUrl));
  const selected = new Set(props.values);
  const parsed = checkboxGroupConfigSchema.safeParse(props.option.config ?? {});
  const max = parsed.success ? parsed.data.maxSelections ?? null : null;
  const atMax = max != null && props.values.length >= max;
  return /* @__PURE__ */ jsx("div", { class: showImages ? "choices cards" : "choices", role: "group", "aria-label": props.option.displayName, children: choices.map((choice) => /* @__PURE__ */ jsxs("label", { class: showImages ? "choice card" : "choice", children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "checkbox",
        checked: selected.has(choice.key),
        disabled: !selected.has(choice.key) && atMax,
        onChange: () => props.onToggle(choice.key)
      }
    ),
    showImages && choice.imageUrl ? /* @__PURE__ */ jsx("img", { src: choice.imageUrl, alt: "" }) : null,
    /* @__PURE__ */ jsx("span", { children: choice.label })
  ] }, choice.key)) });
}
function DimensionInput(props) {
  const parsed = customSizeConfigSchema2.safeParse(props.option.config ?? {});
  if (!parsed.success) return /* @__PURE__ */ jsx("p", { class: "error", children: "This size option is misconfigured." });
  const config = parsed.data;
  const count = config.dimensions;
  const axes = ["x", "y", "z"].slice(0, count);
  return /* @__PURE__ */ jsx("div", { class: "dims", children: axes.map((axis, index) => /* @__PURE__ */ jsxs("label", { class: "dim", children: [
    /* @__PURE__ */ jsx("span", { children: config.labels[axis] ?? "Depth" }),
    /* @__PURE__ */ jsxs("span", { class: "dim-input", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "number",
          inputMode: "decimal",
          min: config.min[axis],
          max: config.max[axis],
          step: config.step[axis],
          value: props.values[index] ?? "",
          onInput: (event) => {
            const next = [props.values[0] ?? "", props.values[1] ?? "", props.values[2] ?? ""];
            next[index] = event.currentTarget.value;
            props.onChange(next.slice(0, count));
          }
        }
      ),
      /* @__PURE__ */ jsx("span", { children: config.unit })
    ] })
  ] }, axis)) });
}
function AxisControl(props) {
  const ui = props.axis.uiType;
  if (ui === "DROPDOWN" || ui !== "COLOR_SWATCH" && ui !== "BUTTON_GROUP" && ui !== "RADIO" && ui !== "IMAGE_CARD") {
    return /* @__PURE__ */ jsxs("select", { value: props.value, onChange: (event) => props.onChange(event.currentTarget.value), children: [
      /* @__PURE__ */ jsx("option", { value: "", children: "Select" }),
      props.axis.values.map((item) => /* @__PURE__ */ jsx("option", { value: item.key, children: item.label }, item.key))
    ] });
  }
  return /* @__PURE__ */ jsx("div", { class: "axes", role: "group", "aria-label": props.axis.name, children: props.axis.values.map((item) => /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      class: ui === "IMAGE_CARD" ? "choice card" : ui === "COLOR_SWATCH" ? "swatch" : "axis",
      "aria-pressed": props.value === item.key,
      "aria-label": item.label,
      style: ui === "COLOR_SWATCH" ? item.swatchUrl ? safeBackground(item.swatchUrl) : item.colorHex ? { background: item.colorHex } : void 0 : void 0,
      onClick: () => props.onChange(item.key),
      children: [
        ui === "IMAGE_CARD" && item.swatchUrl ? /* @__PURE__ */ jsx("img", { src: item.swatchUrl, alt: "" }) : null,
        ui === "COLOR_SWATCH" ? null : item.label
      ]
    },
    item.key
  )) });
}
var RECENT_CELL = 128;
function UploadLibrary(props) {
  const grid = useRef(null);
  const report = useRef(props.onPageSize);
  report.current = props.onPageSize;
  const search = useRef(props.onSearch);
  search.current = props.onSearch;
  const [text, setText] = useState(props.query);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (text.trim() !== props.query.trim()) search.current(text);
    }, 300);
    return () => clearTimeout(handle);
  }, [text, props.query]);
  useEffect(() => {
    if (props.tab !== "RECENT") return;
    const el = grid.current;
    if (!el) return;
    const apply = () => {
      const width = el.clientWidth;
      const columns = width < 40 ? 4 : Math.max(2, Math.min(6, Math.floor((width + 8) / RECENT_CELL)));
      report.current(columns * 2);
    };
    apply();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [props.tab]);
  const tabs = props.sources.filter((source) => source === "DEVICE" || source === "RECENT");
  const pages = Math.max(1, Math.ceil(props.total / Math.max(1, props.pageSize)));
  const empty = props.loaded && props.items.length === 0 && !props.error;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs("div", { class: "lib", children: [
      tabs.length > 1 ? /* @__PURE__ */ jsx("div", { class: "tabs", role: "tablist", children: tabs.map((tab) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          class: "tab",
          role: "tab",
          "aria-selected": props.tab === tab,
          onClick: () => props.onTab(tab),
          children: tab === "DEVICE" ? "Device" : "Recent"
        },
        tab
      )) }) : null,
      props.tab === "RECENT" ? /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            "aria-label": "Search files",
            placeholder: "Search files",
            value: text,
            onInput: (event) => setText(event.currentTarget.value)
          }
        ),
        props.error ? /* @__PURE__ */ jsx("p", { class: "error", children: props.error }) : null,
        empty ? /* @__PURE__ */ jsx("p", { class: "help", children: props.query.trim() ? "No matches." : "No recent files." }) : null,
        /* @__PURE__ */ jsx("div", { class: "recent-grid", ref: grid, children: props.items.map((item) => /* @__PURE__ */ jsxs("button", { type: "button", class: "recent-card", onClick: () => props.onUse(item), children: [
          item.thumbUrl ? /* @__PURE__ */ jsx("img", { src: item.thumbUrl, alt: "" }) : /* @__PURE__ */ jsx("span", { class: "file-icon", "aria-hidden": "true", children: "File" }),
          item.isDraft ? /* @__PURE__ */ jsx("span", { class: "chip warn", children: "Draft" }) : null,
          /* @__PURE__ */ jsx("span", { class: "recent-name", children: item.fileName || "File" })
        ] }, item.token)) }),
        props.total > props.pageSize ? /* @__PURE__ */ jsxs("div", { class: "row", children: [
          /* @__PURE__ */ jsx("button", { type: "button", class: "choice", disabled: props.page <= 1, onClick: () => props.onPage(props.page - 1), children: "Prev" }),
          /* @__PURE__ */ jsxs("span", { class: "help", children: [
            props.page,
            "/",
            pages
          ] }),
          /* @__PURE__ */ jsx("button", { type: "button", class: "choice", disabled: props.page >= pages, onClick: () => props.onPage(props.page + 1), children: "Next" })
        ] }) : null
      ] }) : null
    ] }),
    props.tab === "DEVICE" ? props.children : null
  ] });
}
function UploadPanel(props) {
  const limits = uploadLimits(props.option);
  const atCapacity = limits != null && props.files.length >= limits.maxFiles;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(UploadLibrary, { ...props.library, children: atCapacity ? /* @__PURE__ */ jsx("p", { class: "help", children: "Remove a file to add another." }) : /* @__PURE__ */ jsx(
      DeviceDrop,
      {
        accept: limits ? uploadAcceptAttribute(limits.mimeTypes) : "",
        multiple: (limits?.maxFiles ?? 1) > 1,
        hint: uploadHint(limits),
        onFiles: props.onFiles
      }
    ) }),
    props.files.map((file) => {
      const percent = Math.min(100, Math.max(0, Math.round(file.progress * 100)));
      const uploading = file.status === "uploading";
      const processing = file.status === "processing";
      const meta = [
        file.mimeType,
        formatBytes(file.sizeBytes),
        file.widthPx && file.heightPx ? `${file.widthPx}\xD7${file.heightPx}` : null
      ].filter(Boolean).join(" \xB7 ");
      return /* @__PURE__ */ jsxs("div", { class: "file", children: [
        file.thumbUrl ? /* @__PURE__ */ jsx("img", { class: "file-thumb", src: file.thumbUrl, alt: "" }) : /* @__PURE__ */ jsx("span", { class: "file-icon", "aria-hidden": "true", children: "File" }),
        /* @__PURE__ */ jsxs("div", { class: "file-body", children: [
          /* @__PURE__ */ jsx("div", { class: "file-name", children: file.name }),
          meta ? /* @__PURE__ */ jsx("p", { class: "meta", children: meta }) : null,
          uploading || processing ? /* @__PURE__ */ jsx(
            "div",
            {
              class: processing ? "bar indeterminate" : "bar",
              role: "progressbar",
              "aria-valuemin": 0,
              "aria-valuemax": 100,
              "aria-valuenow": processing ? void 0 : percent,
              "aria-label": processing ? "Checking the file" : `Uploading ${percent}%`,
              children: /* @__PURE__ */ jsx("span", { style: { width: `${processing ? 35 : Math.max(8, percent)}%` } })
            }
          ) : null,
          uploading ? /* @__PURE__ */ jsxs("p", { class: "chip", children: [
            "Uploading ",
            percent,
            "%"
          ] }) : null,
          processing ? /* @__PURE__ */ jsx("p", { class: "chip", children: "Checking" }) : null,
          file.status === "ready" ? /* @__PURE__ */ jsx("p", { class: "chip ready", children: "Ready" }) : null,
          file.status === "failed" ? /* @__PURE__ */ jsx("p", { class: "chip failed", children: "Failed" }) : null,
          file.error ? /* @__PURE__ */ jsx("p", { class: "error", children: file.error }) : null
        ] }),
        /* @__PURE__ */ jsx("button", { type: "button", class: "link", onClick: () => props.onRemove(file.localId), children: "Remove" })
      ] }, file.localId);
    })
  ] });
}
function uploadHint(limits) {
  if (!limits) return "PNG, JPG, PDF";
  const kinds = limits.mimeTypes.map((mime) => mime.split("/")[1]?.toUpperCase() ?? mime).slice(0, 4).join(", ");
  return `${kinds || "Files"} \xB7 up to ${limits.maxFileMb} MB \xB7 max ${limits.maxFiles} files`;
}
function formatBytes(size) {
  if (size == null || !Number.isFinite(size) || size < 0) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
function DeviceDrop(props) {
  return /* @__PURE__ */ jsxs(
    "label",
    {
      class: "drop",
      onDragOver: (event) => {
        event.preventDefault();
        event.currentTarget.classList.add("over");
      },
      onDragLeave: (event) => event.currentTarget.classList.remove("over"),
      onDrop: (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove("over");
        props.onFiles([...event.dataTransfer?.files ?? []]);
      },
      children: [
        /* @__PURE__ */ jsx("svg", { class: "drop-icon", viewBox: "0 0 24 24", width: "28", height: "28", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M12 16V4m0 0 4 4m-4-4-4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }),
        /* @__PURE__ */ jsx("span", { class: "drop-title", children: "Drop a file here" }),
        /* @__PURE__ */ jsx("span", { class: "drop-action", children: "Browse" }),
        /* @__PURE__ */ jsx("span", { class: "help", children: props.hint }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "file",
            accept: props.accept,
            multiple: props.multiple,
            onChange: (event) => {
              const input = event.currentTarget;
              props.onFiles([...input.files ?? []]);
              input.value = "";
            }
          }
        )
      ]
    }
  );
}

// src/sized-measure.ts
import {
  detectedSizeIn,
  fitSize,
  gangsheetFixedWidthIn,
  heightFromWidth,
  heightMatchesTrimmedRatio,
  resolveEffectiveDpi,
  roundDisplay,
  sizingBounds,
  uploadConfigSchema as uploadConfigSchema2,
  widthFromHeight
} from "@tkpod/shared/product-options";
function parseSizedConfig(config) {
  const parsed = uploadConfigSchema2.safeParse(config ?? {});
  return parsed.success ? parsed.data : null;
}
function measurementFromStatus(optionType, config, status) {
  const trimmedWidthPx = status.trimmedWidthPx;
  const trimmedHeightPx = status.trimmedHeightPx;
  if (trimmedWidthPx == null || trimmedHeightPx == null || trimmedWidthPx <= 0 || trimmedHeightPx <= 0) {
    return null;
  }
  const assumed = config.sizing?.assumedDpi ?? 300;
  const fileDpi = resolveEffectiveDpi({
    dpiSource: "EMBEDDED_IF_PLAUSIBLE",
    assumedDpi: assumed,
    embeddedDpi: status.embeddedDpi ?? null
  });
  const dpi = status.embeddedDpi != null ? fileDpi : status.effectiveDpi != null && status.effectiveDpi > 0 ? status.effectiveDpi : fileDpi;
  const trimmed = { widthPx: trimmedWidthPx, heightPx: trimmedHeightPx };
  const detected = detectedSizeIn({ trimmed, dpi });
  const nativeWidthIn = detected.widthIn;
  const nativeHeightIn = detected.heightIn;
  const hasAlpha = typeof status.hasAlpha === "boolean" ? status.hasAlpha : null;
  const cap = optionType === "GANGSHEET_UPLOAD_SIZED" ? gangsheetFixedWidthIn(config.variantBinding?.fixedWidthIn) : void 0;
  const fitted = detectedFit(config, { trimmed, nativeWidthIn, nativeHeightIn }, cap);
  return {
    presetId: "detected",
    widthIn: fitted.widthIn,
    heightIn: fitted.heightIn,
    ratioLocked: true,
    stretched: false,
    trimmedWidthPx,
    trimmedHeightPx,
    effectiveDpi: dpi,
    nativeWidthIn: roundDisplay(nativeWidthIn),
    nativeHeightIn: roundDisplay(nativeHeightIn),
    hasAlpha
  };
}
function sizedOptionShowsVariantPicker(config) {
  if (!config || typeof config !== "object") return false;
  const binding = config.variantBinding;
  return binding?.customerCanChangeVariant === true;
}
function detectedFit(config, source, maxWidthIn) {
  const bounds = sizingBounds(config);
  const capped = maxWidthIn != null && maxWidthIn > 0;
  const maxWidth = capped ? Math.min(bounds.maxWidthIn, maxWidthIn) : bounds.maxWidthIn;
  const fitted = fitSize({
    ...bounds,
    minSizeIn: capped ? Math.min(bounds.minSizeIn, 0.01) : bounds.minSizeIn,
    maxWidthIn: maxWidth,
    widthIn: source.nativeWidthIn,
    heightIn: source.nativeHeightIn,
    ratio: source.trimmed.widthPx / source.trimmed.heightPx,
    ratioLocked: true,
    edited: "width"
  });
  return { widthIn: roundDisplay(fitted.widthIn), heightIn: roundDisplay(fitted.heightIn) };
}
function applySizeEdit(config, source, current, edit) {
  const trimmed = source.trimmed;
  if (trimmed.widthPx <= 0 || trimmed.heightPx <= 0) return current;
  const bounds = sizingBounds(config);
  const ratio = trimmed.widthPx / trimmed.heightPx;
  const allowStretch = config.sizing?.allowStretch ?? true;
  const finish = (widthIn, heightIn, presetId, ratioLocked, edited) => {
    const fitted = fitSize({ ...bounds, widthIn, heightIn, ratio, ratioLocked, edited });
    const width = roundDisplay(fitted.widthIn);
    const height = roundDisplay(fitted.heightIn);
    return {
      widthIn: width,
      heightIn: height,
      presetId,
      ratioLocked,
      stretched: !ratioLocked && !heightMatchesTrimmedRatio(width, height, trimmed)
    };
  };
  switch (edit.kind) {
    case "detected": {
      return finish(source.nativeWidthIn, source.nativeHeightIn, "detected", true, "width");
    }
    case "preset": {
      if (!Number.isFinite(edit.widthIn) || edit.widthIn <= 0) return current;
      return finish(edit.widthIn, heightFromWidth(edit.widthIn, trimmed), edit.presetId, true, "width");
    }
    case "custom": {
      if (current.presetId === "custom") return current;
      return { ...current, presetId: "custom" };
    }
    case "lock": {
      const locked = edit.locked || !allowStretch;
      if (locked === current.ratioLocked) return current;
      if (locked) {
        return finish(current.widthIn, heightFromWidth(current.widthIn, trimmed), current.presetId, true, "width");
      }
      return { ...current, ratioLocked: false, stretched: false };
    }
    case "step": {
      const step = bounds.stepIn > 0 ? bounds.stepIn : 0.1;
      const base = edit.axis === "width" ? current.widthIn : current.heightIn;
      const next = roundDisplay(base + edit.direction * step);
      return applySizeEdit(config, source, current, {
        kind: edit.axis,
        inches: next
      });
    }
    case "width": {
      if (!Number.isFinite(edit.inches) || edit.inches <= 0) return current;
      if (current.ratioLocked) {
        return finish(edit.inches, heightFromWidth(edit.inches, trimmed), "custom", true, "width");
      }
      return finish(edit.inches, current.heightIn, "custom", false, "width");
    }
    case "height": {
      if (!Number.isFinite(edit.inches) || edit.inches <= 0) return current;
      if (current.ratioLocked) {
        const width = widthFromHeight(edit.inches, trimmed);
        return finish(width, heightFromWidth(width, trimmed), "custom", true, "width");
      }
      return finish(current.widthIn, edit.inches, "custom", false, "height");
    }
    default: {
      const unreachable = edit;
      return unreachable;
    }
  }
}

// src/sized-upload.tsx
import { useEffect as useEffect2, useState as useState2 } from "preact/hooks";
import {
  activeQuantityTier,
  dpiQuality,
  effectivePrintDpi,
  formatInches,
  roundDisplay as roundDisplay2,
  sizingBounds as sizingBounds2
} from "@tkpod/shared/product-options";
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "preact/jsx-runtime";
var PREVIEW_SWATCHES = ["checker", "#ffffff", "#111111", "#9ca3af"];
function SizedUploadPanel(props) {
  const gang = props.optionType === "GANGSHEET_UPLOAD_SIZED";
  const sizing = props.config.sizing;
  const allowResize = !gang && (sizing?.allowResize ?? true);
  const allowStretch = allowResize && (sizing?.allowStretch ?? true);
  const presets = (sizing?.presets ?? []).filter((preset) => preset.active);
  const bounds = sizingBounds2(props.config);
  const bandPricing = props.config.sizePricing?.mode === "VARIANT_BAND";
  const [dragOver, setDragOver] = useState2(false);
  return /* @__PURE__ */ jsxs2("div", { class: "sized", children: [
    props.files.map((file, index) => /* @__PURE__ */ jsx2(
      SizedCard,
      {
        index: index + 1,
        file,
        gang,
        allowResize,
        allowStretch,
        allowDetected: sizing?.allowDetectedSize !== false,
        presets,
        bounds,
        dpiGood: sizing?.dpiGood ?? 250,
        dpiFair: sizing?.dpiFair ?? 150,
        minDpiBlock: props.config.printRender?.minEffectiveDpiBlock ?? null,
        minQty: props.block.quantity.min,
        maxQty: props.block.quantity.max,
        stepQty: props.block.quantity.step,
        accept: props.accept,
        config: props.config,
        bandPricing,
        quote: props.quoteLines?.find((line) => line.id === file.localId) ?? null,
        formatMoney: props.formatMoney,
        canDuplicate: !props.atCapacity,
        onReplace: (picked) => props.onReplace(file.localId, picked),
        onDuplicate: () => props.onDuplicate(file.localId),
        onRemove: () => props.onRemove(file.localId),
        onPatch: (patch) => props.onPatch(file.localId, patch)
      },
      file.localId
    )),
    /* @__PURE__ */ jsx2(UploadLibrary, { ...props.library, children: props.atCapacity ? /* @__PURE__ */ jsx2("p", { class: "help", children: "Remove an image to add another." }) : /* @__PURE__ */ jsxs2(
      "label",
      {
        class: `drop${dragOver ? " over" : ""}${props.files.length > 0 ? " compact" : ""}`,
        onDragOver: (event) => {
          event.preventDefault();
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (event) => {
          event.preventDefault();
          setDragOver(false);
          const dropped = [...event.dataTransfer?.files ?? []];
          if (dropped.length > 0) props.onFiles(dropped);
        },
        children: [
          /* @__PURE__ */ jsx2("span", { class: "drop-title", children: props.files.length > 0 ? "Add more images" : gang ? "Drop your gang sheet here" : "Drop your images here" }),
          /* @__PURE__ */ jsx2("span", { class: "drop-action", children: props.files.length > 0 ? `${props.maxFiles - props.files.length} more allowed \xB7 drop or click` : `Up to ${props.maxFiles} ${props.maxFiles === 1 ? "file" : "files"} \xB7 drop or click` }),
          /* @__PURE__ */ jsx2(
            "input",
            {
              type: "file",
              accept: props.accept,
              multiple: !gang && props.maxFiles > 1,
              onChange: (event) => {
                const input = event.currentTarget;
                const picked = [...input.files ?? []];
                input.value = "";
                if (picked.length > 0) props.onFiles(picked);
              }
            }
          )
        ]
      }
    ) })
  ] });
}
function SizedCard(props) {
  const file = props.file;
  const busy = file.status === "uploading" || file.status === "processing";
  const percent = Math.min(100, Math.max(0, Math.round(file.progress * 100)));
  const trimmed = file.trimmedWidthPx != null && file.trimmedHeightPx != null && file.trimmedWidthPx > 0 && file.trimmedHeightPx > 0 ? { widthPx: file.trimmedWidthPx, heightPx: file.trimmedHeightPx } : null;
  const measured = file.status === "ready" && trimmed != null && file.widthIn != null && file.heightIn != null;
  const widthIn = file.widthIn ?? props.bounds.minSizeIn;
  const heightIn = file.heightIn ?? props.bounds.minSizeIn;
  const ratioLocked = file.ratioLocked !== false;
  const stretched = file.stretched === true;
  const presetId = file.presetId || "detected";
  const qty = file.quantity ?? props.minQty;
  const source = trimmed && file.nativeWidthIn != null && file.nativeHeightIn != null ? { trimmed, nativeWidthIn: file.nativeWidthIn, nativeHeightIn: file.nativeHeightIn } : null;
  const detected = source ? detectedFit(props.config, source) : null;
  const fileWasFitted = detected != null && source != null && (roundDisplay2(source.nativeWidthIn) !== detected.widthIn || roundDisplay2(source.nativeHeightIn) !== detected.heightIn);
  const wasTrimmed = trimmed != null && file.widthPx != null && file.heightPx != null && (file.widthPx !== trimmed.widthPx || file.heightPx !== trimmed.heightPx);
  const printDpi = measured ? props.gang ? file.effectiveDpi ?? 0 : stretched ? Math.min(
    effectivePrintDpi(trimmed.widthPx, widthIn),
    effectivePrintDpi(trimmed.heightPx, heightIn)
  ) : effectivePrintDpi(trimmed.widthPx, widthIn) : 0;
  const quality = dpiQuality(printDpi, props.dpiGood, props.dpiFair);
  const dpiBlocked = measured && props.minDpiBlock != null && printDpi > 0 && printDpi < props.minDpiBlock;
  const previewBg = file.previewBg ?? "checker";
  const edit = (change) => {
    if (!source) return;
    const current = { widthIn, heightIn, presetId, ratioLocked, stretched };
    const next = applySizeEdit(props.config, source, current, change);
    if (next.widthIn === current.widthIn && next.heightIn === current.heightIn && next.presetId === current.presetId && next.ratioLocked === current.ratioLocked && next.stretched === current.stretched) {
      return;
    }
    props.onPatch({
      widthIn: next.widthIn,
      heightIn: next.heightIn,
      presetId: next.presetId,
      ratioLocked: next.ratioLocked,
      stretched: next.stretched
    });
  };
  const setQty = (value) => {
    if (!Number.isFinite(value)) return;
    const stepped = Math.round(value / props.stepQty) * props.stepQty;
    props.onPatch({ quantity: Math.min(props.maxQty, Math.max(props.minQty, stepped)) });
  };
  const lockTip = !props.allowStretch ? "Width and height keep the image proportions." : ratioLocked ? "Proportions locked. Click to set width and height separately." : "Proportions unlocked. The artwork is stretched to the size you enter. Click to lock.";
  return /* @__PURE__ */ jsxs2("article", { class: `s-card${busy ? " busy" : ""}${file.status === "failed" ? " failed" : ""}`, children: [
    /* @__PURE__ */ jsxs2("header", { class: "s-head", children: [
      /* @__PURE__ */ jsx2("span", { class: "s-num", children: props.index }),
      /* @__PURE__ */ jsx2("span", { class: "s-name", title: file.name, children: file.name }),
      file.status === "ready" || file.status === "failed" ? /* @__PURE__ */ jsxs2("label", { class: "s-ib", title: "Replace file", "aria-label": "Replace file", children: [
        /* @__PURE__ */ jsx2(Icon, { d: "M4 12a8 8 0 1 1 .7-5.7L20 8M20 4v4h-4M20 12a8 8 0 1 1-.7 5.7L4 16M4 20v-4h4" }),
        /* @__PURE__ */ jsx2(
          "input",
          {
            type: "file",
            accept: props.accept,
            onChange: (event) => {
              const input = event.currentTarget;
              const picked = input.files?.[0];
              input.value = "";
              if (picked) props.onReplace(picked);
            }
          }
        )
      ] }) : null,
      props.gang ? null : /* @__PURE__ */ jsx2(
        "button",
        {
          type: "button",
          class: "s-ib",
          title: "Duplicate",
          "aria-label": "Duplicate",
          disabled: !measured || !props.canDuplicate,
          onClick: props.onDuplicate,
          children: /* @__PURE__ */ jsx2(Icon, { d: "M9 9h11v11H9zM15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" })
        }
      ),
      /* @__PURE__ */ jsx2("button", { type: "button", class: "s-ib del", title: "Remove", "aria-label": "Remove", onClick: props.onRemove, children: /* @__PURE__ */ jsx2(Icon, { d: "M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" }) })
    ] }),
    /* @__PURE__ */ jsxs2("div", { class: "s-body", children: [
      /* @__PURE__ */ jsxs2("div", { class: "s-prevcol", children: [
        /* @__PURE__ */ jsxs2(
          "div",
          {
            class: `s-preview${previewBg === "checker" ? " checker" : ""}`,
            style: previewBg === "checker" ? void 0 : { background: previewBg },
            children: [
              file.thumbUrl && !busy ? /* @__PURE__ */ jsx2("img", { src: file.thumbUrl, alt: "" }) : null,
              busy ? /* @__PURE__ */ jsxs2("div", { class: "s-skel", children: [
                /* @__PURE__ */ jsx2(
                  "div",
                  {
                    class: "bar",
                    role: "progressbar",
                    "aria-valuemin": 0,
                    "aria-valuemax": 100,
                    "aria-valuenow": percent,
                    "aria-label": file.status === "processing" ? "Detecting size" : `Uploading ${percent}%`,
                    children: /* @__PURE__ */ jsx2("span", { style: { width: `${percent}%` } })
                  }
                ),
                /* @__PURE__ */ jsx2("span", { children: file.status === "processing" ? "Reading file, trimming edges\u2026" : `Uploading ${percent}%` })
              ] }) : null,
              measured ? /* @__PURE__ */ jsxs2("div", { class: "s-bgbar", children: [
                PREVIEW_SWATCHES.map((swatch) => /* @__PURE__ */ jsx2(
                  "button",
                  {
                    type: "button",
                    class: `s-sw${swatch === "checker" ? " checker" : ""}${previewBg === swatch ? " on" : ""}`,
                    style: swatch === "checker" ? void 0 : { background: swatch },
                    "aria-label": `Preview background ${swatch === "checker" ? "transparent" : swatch}`,
                    "aria-pressed": previewBg === swatch,
                    onClick: () => props.onPatch({ previewBg: swatch })
                  },
                  swatch
                )),
                /* @__PURE__ */ jsx2(
                  "label",
                  {
                    class: `s-sw custom${previewBg.startsWith("#") && !PREVIEW_SWATCHES.includes(previewBg) ? " on" : ""}`,
                    title: "Custom color",
                    children: /* @__PURE__ */ jsx2(
                      "input",
                      {
                        type: "color",
                        value: previewBg.startsWith("#") ? previewBg : "#ff5a5f",
                        "aria-label": "Custom preview background",
                        onInput: (event) => props.onPatch({ previewBg: event.currentTarget.value })
                      }
                    )
                  }
                )
              ] }) : null
            ]
          }
        ),
        /* @__PURE__ */ jsx2("div", { class: "s-prevsize", children: measured ? `${formatInches(widthIn)}\u2033 \xD7 ${formatInches(heightIn)}\u2033` : "" })
      ] }),
      /* @__PURE__ */ jsxs2("div", { class: "s-ctrl", children: [
        props.gang ? /* @__PURE__ */ jsxs2("p", { class: "meta", children: [
          "Sheet ",
          formatInches(widthIn),
          "\u2033 wide \xD7 ",
          formatInches(heightIn),
          "\u2033 tall"
        ] }) : /* @__PURE__ */ jsxs2("div", { class: "s-dims", children: [
          /* @__PURE__ */ jsx2(
            AxisInput,
            {
              label: "Width",
              hint: `max ${trimTrailing(props.bounds.maxWidthIn)}\u2033`,
              inches: widthIn,
              disabled: !measured || !props.allowResize,
              onCommit: (inches) => edit({ kind: "width", inches }),
              onStep: (direction) => edit({ kind: "step", axis: "width", direction })
            }
          ),
          /* @__PURE__ */ jsx2(
            "button",
            {
              type: "button",
              class: `s-lock${ratioLocked ? "" : " open"}`,
              "aria-pressed": ratioLocked,
              "aria-label": ratioLocked ? "Proportions locked" : "Proportions unlocked",
              "data-tip": lockTip,
              disabled: !measured || !props.allowStretch,
              onClick: () => edit({ kind: "lock", locked: !ratioLocked }),
              children: /* @__PURE__ */ jsx2(Icon, { d: ratioLocked ? "M8 11V8a4 4 0 0 1 8 0v3M5 11h14v9H5z" : "M8 11V8a4 4 0 0 1 7.5-2M5 11h14v9H5z" })
            }
          ),
          /* @__PURE__ */ jsx2(
            AxisInput,
            {
              label: "Height",
              hint: `max ${trimTrailing(props.bounds.maxHeightIn)}\u2033`,
              inches: heightIn,
              disabled: !measured || !props.allowResize,
              onCommit: (inches) => edit({ kind: "height", inches }),
              onStep: (direction) => edit({ kind: "step", axis: "height", direction })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs2("div", { class: "s-row2", children: [
          props.gang ? null : /* @__PURE__ */ jsxs2("label", { class: "s-field", children: [
            /* @__PURE__ */ jsx2("span", { children: "Size" }),
            /* @__PURE__ */ jsxs2(
              "select",
              {
                value: presetId,
                disabled: !measured || !props.allowResize,
                "aria-label": "Size preset",
                onChange: (event) => {
                  const value = event.currentTarget.value;
                  if (value === "detected") {
                    edit({ kind: "detected" });
                    return;
                  }
                  if (value === "custom") {
                    edit({ kind: "custom" });
                    return;
                  }
                  const preset = props.presets.find((item) => item.id === value);
                  if (preset) edit({ kind: "preset", presetId: preset.id, widthIn: preset.widthIn });
                },
                children: [
                  props.allowResize ? /* @__PURE__ */ jsx2("option", { value: "custom", children: "Custom size" }) : null,
                  props.allowDetected && detected ? /* @__PURE__ */ jsxs2("option", { value: "detected", children: [
                    "Original \xB7 ",
                    formatInches(detected.widthIn),
                    "\u2033 \xD7 ",
                    formatInches(detected.heightIn),
                    "\u2033",
                    fileWasFitted ? " (fit to max)" : ""
                  ] }) : null,
                  props.presets.map((preset) => /* @__PURE__ */ jsxs2("option", { value: preset.id, children: [
                    preset.label,
                    " \xB7 ",
                    trimTrailing(preset.widthIn),
                    "\u2033 \xD7",
                    " ",
                    trimmed ? formatInches(preset.widthIn * trimmed.heightPx / trimmed.widthPx) : "\u2014",
                    "\u2033"
                  ] }, preset.id))
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs2("div", { class: "s-field", children: [
            /* @__PURE__ */ jsx2("span", { children: "Qty" }),
            /* @__PURE__ */ jsxs2("div", { class: "s-step", children: [
              /* @__PURE__ */ jsx2(
                "button",
                {
                  type: "button",
                  "aria-label": "Decrease quantity",
                  disabled: !measured || qty <= props.minQty,
                  onClick: () => setQty(qty - props.stepQty),
                  children: "\u2212"
                }
              ),
              /* @__PURE__ */ jsx2(QtyInput, { value: qty, disabled: !measured, onCommit: setQty }),
              /* @__PURE__ */ jsx2(
                "button",
                {
                  type: "button",
                  "aria-label": "Increase quantity",
                  disabled: !measured || qty >= props.maxQty,
                  onClick: () => setQty(qty + props.stepQty),
                  children: "+"
                }
              )
            ] })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs2("div", { class: "s-info", children: [
        /* @__PURE__ */ jsx2("div", { class: "s-chips", children: measured ? /* @__PURE__ */ jsxs2(Fragment, { children: [
          /* @__PURE__ */ jsxs2("span", { class: `chip dpi ${quality}`, children: [
            Math.min(999, Math.round(printDpi)),
            " DPI \xB7",
            " ",
            quality === "good" ? "print-ready" : quality === "fair" ? "may look soft" : "low resolution"
          ] }),
          file.hasAlpha === true ? /* @__PURE__ */ jsx2("span", { class: "chip ok", children: "Transparent background" }) : file.hasAlpha === false ? /* @__PURE__ */ jsx2("span", { class: "chip warn", children: "No transparency" }) : null,
          wasTrimmed ? /* @__PURE__ */ jsx2("span", { class: "chip", title: "Empty edges were trimmed. You pay for the artwork only.", children: "Trimmed" }) : null,
          stretched ? /* @__PURE__ */ jsx2("span", { class: "chip warn", title: "Width and height no longer follow the image proportions.", children: "Stretched" }) : null,
          source ? /* @__PURE__ */ jsxs2("span", { class: "chip", title: "Detected from your file after trimming empty edges", children: [
            "Original ",
            formatInches(source.nativeWidthIn),
            "\u2033 \xD7 ",
            formatInches(source.nativeHeightIn),
            "\u2033"
          ] }) : null
        ] }) : null }),
        /* @__PURE__ */ jsx2("div", { class: "s-price", children: measured && props.quote ? /* @__PURE__ */ jsxs2(Fragment, { children: [
          /* @__PURE__ */ jsxs2("div", { class: "s-price-l", children: [
            props.formatMoney(props.quote.unitCents),
            " ",
            /* @__PURE__ */ jsxs2("span", { children: [
              "each",
              props.bandPricing && props.quote.variant?.title ? ` \xB7 ${props.quote.variant.title}` : ""
            ] }),
            props.quote.listUnitCents > props.quote.unitCents ? /* @__PURE__ */ jsx2("s", { children: props.formatMoney(props.quote.listUnitCents) }) : null
          ] }),
          /* @__PURE__ */ jsxs2("div", { class: "s-price-r", children: [
            "\xD7 ",
            qty,
            " = ",
            /* @__PURE__ */ jsx2("b", { children: props.formatMoney(props.quote.lineTotalCents) })
          ] })
        ] }) : null })
      ] })
    ] }),
    file.status === "failed" && file.error ? /* @__PURE__ */ jsx2("p", { class: "error s-err", children: file.error }) : null,
    dpiBlocked ? /* @__PURE__ */ jsxs2("p", { class: "error s-err", children: [
      "This print would be ",
      Math.round(printDpi),
      " DPI. The minimum is ",
      props.minDpiBlock,
      "."
    ] }) : null
  ] });
}
function AxisInput(props) {
  const [draft, setDraft] = useState2(null);
  const shown = draft ?? formatInches(props.inches);
  useEffect2(() => {
    setDraft(null);
  }, [props.inches]);
  const commit = () => {
    if (draft == null) return;
    const parsed = Number(draft.replace(",", ".").trim());
    setDraft(null);
    if (Number.isFinite(parsed) && parsed > 0) props.onCommit(parsed);
  };
  return /* @__PURE__ */ jsxs2("label", { class: "s-field s-axis", children: [
    /* @__PURE__ */ jsxs2("span", { children: [
      props.label,
      " ",
      /* @__PURE__ */ jsx2("small", { children: props.hint })
    ] }),
    /* @__PURE__ */ jsxs2("span", { class: "s-step", children: [
      /* @__PURE__ */ jsx2(
        "button",
        {
          type: "button",
          "aria-label": `Decrease ${props.label.toLowerCase()}`,
          disabled: props.disabled,
          onClick: () => props.onStep(-1),
          children: "\u2212"
        }
      ),
      /* @__PURE__ */ jsx2(
        "input",
        {
          type: "text",
          inputMode: "decimal",
          autoComplete: "off",
          "aria-label": `${props.label} in inches`,
          value: shown,
          disabled: props.disabled,
          onInput: (event) => setDraft(event.currentTarget.value),
          onChange: commit,
          onBlur: commit,
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }
        }
      ),
      /* @__PURE__ */ jsx2(
        "button",
        {
          type: "button",
          "aria-label": `Increase ${props.label.toLowerCase()}`,
          disabled: props.disabled,
          onClick: () => props.onStep(1),
          children: "+"
        }
      )
    ] })
  ] });
}
function QtyInput(props) {
  const [draft, setDraft] = useState2(null);
  useEffect2(() => {
    setDraft(null);
  }, [props.value]);
  const commit = () => {
    if (draft == null) return;
    const parsed = Number(draft.trim());
    setDraft(null);
    if (Number.isFinite(parsed)) props.onCommit(Math.round(parsed));
  };
  return /* @__PURE__ */ jsx2(
    "input",
    {
      type: "text",
      inputMode: "numeric",
      autoComplete: "off",
      "aria-label": "Quantity",
      value: draft ?? String(props.value),
      disabled: props.disabled,
      onInput: (event) => setDraft(event.currentTarget.value),
      onChange: commit,
      onBlur: commit,
      onKeyDown: (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }
    }
  );
}
function SizedSummary(props) {
  const transfers = props.lines.reduce((sum, line) => sum + line.quantity, 0);
  const list = props.lines.reduce((sum, line) => sum + line.listUnitCents * line.quantity, 0);
  const total = props.lines.reduce((sum, line) => sum + line.lineTotalCents, 0);
  const discount = Math.max(0, list - total);
  const tiers = [...props.tiers].sort((a, b) => a.minQty - b.minQty);
  const active = activeQuantityTier(tiers, transfers);
  const next = tiers.find((tier) => tier.minQty > transfers && tier.discountPercent > (active?.discountPercent ?? 0));
  if (props.lines.length === 0) return null;
  return /* @__PURE__ */ jsxs2("section", { class: "s-summary", "aria-label": "Order summary", children: [
    tiers.length > 0 ? next ? /* @__PURE__ */ jsxs2("div", { class: "s-nudge", children: [
      "Add ",
      /* @__PURE__ */ jsx2("b", { children: next.minQty - transfers }),
      " more ",
      next.minQty - transfers === 1 ? "transfer" : "transfers",
      " to get",
      " ",
      /* @__PURE__ */ jsxs2("b", { children: [
        next.discountPercent,
        "% off"
      ] }),
      " your whole order",
      /* @__PURE__ */ jsx2("div", { class: "s-nudge-bar", children: /* @__PURE__ */ jsx2("i", { style: { width: `${Math.min(100, transfers / next.minQty * 100)}%` } }) })
    ] }) : active && active.discountPercent > 0 ? /* @__PURE__ */ jsxs2("div", { class: "s-nudge", children: [
      "You've unlocked our best price \u2014 ",
      /* @__PURE__ */ jsxs2("b", { children: [
        active.discountPercent,
        "% off"
      ] }),
      " everything."
    ] }) : null : null,
    /* @__PURE__ */ jsx2("p", { class: "s-sum-title", children: "Order summary" }),
    /* @__PURE__ */ jsxs2("div", { class: "s-sum-row", children: [
      /* @__PURE__ */ jsx2("span", { children: "Designs" }),
      /* @__PURE__ */ jsx2("span", { children: props.lines.length })
    ] }),
    /* @__PURE__ */ jsxs2("div", { class: "s-sum-row", children: [
      /* @__PURE__ */ jsx2("span", { children: "Transfers" }),
      /* @__PURE__ */ jsx2("span", { children: transfers })
    ] }),
    /* @__PURE__ */ jsxs2("div", { class: "s-sum-row", children: [
      /* @__PURE__ */ jsx2("span", { children: "Subtotal" }),
      /* @__PURE__ */ jsx2("span", { children: props.formatMoney(list) })
    ] }),
    discount > 0 && active ? /* @__PURE__ */ jsxs2("div", { class: "s-sum-row disc", children: [
      /* @__PURE__ */ jsxs2("span", { children: [
        "Volume discount (",
        active.discountPercent,
        "%)"
      ] }),
      /* @__PURE__ */ jsxs2("span", { children: [
        "\u2212",
        props.formatMoney(discount)
      ] })
    ] }) : null,
    /* @__PURE__ */ jsxs2("div", { class: "s-sum-total", children: [
      /* @__PURE__ */ jsxs2("span", { children: [
        /* @__PURE__ */ jsx2("b", { children: "Total" }),
        /* @__PURE__ */ jsx2("small", { children: props.estimated ? "estimated, before shipping" : "before shipping" })
      ] }),
      /* @__PURE__ */ jsx2("b", { children: props.formatMoney(total) })
    ] }),
    tiers.length > 0 ? /* @__PURE__ */ jsxs2("details", { class: "s-tiers", children: [
      /* @__PURE__ */ jsx2("summary", { children: "See volume pricing" }),
      /* @__PURE__ */ jsx2("table", { children: /* @__PURE__ */ jsx2("tbody", { children: tiers.map((tier, index) => {
        const upper = tiers[index + 1];
        return /* @__PURE__ */ jsxs2("tr", { class: active?.minQty === tier.minQty && transfers > 0 ? "on" : "", children: [
          /* @__PURE__ */ jsxs2("td", { children: [
            tier.minQty,
            upper ? `\u2013${upper.minQty - 1}` : "+",
            " transfers"
          ] }),
          /* @__PURE__ */ jsx2("td", { children: tier.discountPercent > 0 ? `${tier.discountPercent}% off` : "List price" })
        ] }, tier.minQty);
      }) }) })
    ] }) : null
  ] });
}
function trimTrailing(value) {
  return String(roundDisplay2(value));
}
function Icon(props) {
  return /* @__PURE__ */ jsx2("svg", { viewBox: "0 0 24 24", width: "16", height: "16", "aria-hidden": "true", children: /* @__PURE__ */ jsx2("path", { d: props.d, fill: "none", stroke: "currentColor", strokeWidth: "2" }) });
}

// src/variant.ts
function selectedAxes(axes) {
  return Object.entries(axes).filter((entry) => entry[1] !== "");
}
function matchingVariants(variants, axes) {
  const selected = selectedAxes(axes);
  return variants.filter(
    (variant) => selected.every(([optionTypeId, valueKey]) => variant.axes[optionTypeId] === valueKey)
  );
}
function variantQueryUrl(href, externalVariantId) {
  if (!/^\d+$/.test(externalVariantId)) return null;
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.searchParams.get("variant") === externalVariantId) return null;
  url.searchParams.set("variant", externalVariantId);
  return `${url.pathname}${url.search}${url.hash}`;
}
function findVariantByRef(variants, variantRef) {
  return variants.find(
    (variant) => variant.externalVariantId === variantRef || variant.productVariantId === variantRef
  );
}

// src/app.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "preact/jsx-runtime";
function defaultSizedVariant(option, variants) {
  if (variants.length === 0) return null;
  const config = option ? parseSizedConfig(option.config) : null;
  const rateId = config?.sizePricing?.pricingVariantId ?? null;
  if (rateId) {
    const rate = variants.find((variant) => variant.blankVariantId === rateId);
    if (rate) return rate;
  }
  return variants[0] ?? null;
}
function axisSelections(axes) {
  return Object.entries(axes).filter((entry) => entry[1] !== "").map(([optionTypeId, valueKey]) => ({ optionTypeId, valueKey }));
}
var CHOICE_TYPES = /* @__PURE__ */ new Set(["RADIO", "SELECT", "SWATCH", "BUTTON_GROUP"]);
var EMPTY_RECENT = { items: [], page: 1, pageSize: 8, total: 0, q: "", loaded: false };
function OptionsApp(props) {
  const [config, setConfig] = useState3(null);
  const [loadError, setLoadError] = useState3(null);
  const [axes, setAxes] = useState3({});
  const [fields, setFields] = useState3({});
  const [files, setFiles] = useState3({});
  const filesRef = useRef2(files);
  filesRef.current = files;
  useEffect3(
    () => () => {
      for (const list of Object.values(filesRef.current)) {
        for (const file of list) {
          file.abort?.abort();
          if (file.thumbUrl?.startsWith("blob:")) URL.revokeObjectURL(file.thumbUrl);
        }
      }
    },
    []
  );
  const [tabs, setTabs] = useState3({});
  const [recent, setRecent] = useState3({});
  const recentRef = useRef2(recent);
  recentRef.current = recent;
  const recentReq = useRef2({});
  const recentLimit = useRef2({});
  const [recentError, setRecentError] = useState3({});
  const [quantity, setQuantity] = useState3(1);
  const [errors, setErrors] = useState3({});
  const [formError, setFormError] = useState3(null);
  const [busy, setBusy] = useState3(false);
  const [added, setAdded] = useState3(false);
  const defaultsApplied = useRef2(/* @__PURE__ */ new Set());
  useEffect3(() => {
    let cancelled = false;
    setConfig(null);
    setLoadError(null);
    props.transport.getConfig({ productRef: props.productRef, uptId: props.uptId }).then((next) => {
      if (cancelled) return;
      setConfig(next);
      const block2 = parseBlock(next.blockConfig);
      setQuantity(block2.quantity.min);
      const preset = props.variantRef ? findVariantByRef(next.variants, props.variantRef) : void 0;
      const presetAxes = preset ? { ...preset.axes } : {};
      setAxes(presetAxes);
      defaultsApplied.current = /* @__PURE__ */ new Set();
      const initiallyVisible = visibleOptions(next.options, {
        selections: {},
        variantAxes: presetAxes,
        quantity: block2.quantity.min
      });
      const seeded = {};
      for (const option of initiallyVisible) {
        defaultsApplied.current.add(option.key);
        const values = initialSelection({
          optionType: option.optionType,
          defaultValue: option.defaultValue,
          values: option.values,
          config: option.config
        });
        if (values.length > 0) seeded[option.key] = { values, uploadTokens: [] };
      }
      setFields(seeded);
      setErrors({});
    }).catch((err) => {
      if (!cancelled) setLoadError(err instanceof Error ? err.message : "Options could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [props.transport, props.productRef, props.uptId, props.variantRef]);
  useEffect3(() => {
    if (!config) return;
    const visible = visibleOptions(config.options, {
      selections: Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.values])),
      variantAxes: axes,
      quantity
    });
    const additions = {};
    for (const option of visible) {
      if (defaultsApplied.current.has(option.key)) continue;
      defaultsApplied.current.add(option.key);
      if (fields[option.key]) continue;
      const values = initialSelection({
        optionType: option.optionType,
        defaultValue: option.defaultValue,
        values: option.values,
        config: option.config
      });
      if (values.length > 0) additions[option.key] = { values, uploadTokens: [] };
    }
    if (Object.keys(additions).length === 0) return;
    setFields((current) => {
      const next = { ...current };
      for (const [key, value] of Object.entries(additions)) {
        if (!next[key]) next[key] = value;
      }
      return next;
    });
  }, [config, fields, axes, quantity]);
  const syncedVariant = useRef2(props.variantRef);
  useEffect3(() => {
    if (props.surface !== "STOREFRONT" || !config?.blockEnabled) return;
    const chosen = matchingVariants(config.variants, axes);
    const externalId = chosen.length === 1 ? chosen[0]?.externalVariantId : null;
    if (!externalId || externalId === syncedVariant.current) return;
    syncedVariant.current = externalId;
    const nextUrl = variantQueryUrl(window.location.href, externalId);
    if (nextUrl) window.history.replaceState(window.history.state, "", nextUrl);
    props.actionTarget.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        composed: true,
        detail: { variantId: externalId }
      })
    );
  }, [props.surface, props.actionTarget, config, axes]);
  useEffect3(() => {
    if (!config?.blockEnabled) return;
    props.actionTarget.dispatchEvent(
      new CustomEvent("tk:options-active", { bubbles: true, composed: true })
    );
  }, [config, props.actionTarget]);
  useEffect3(() => {
    if (!config) return;
    props.actionTarget.dispatchEvent(
      new CustomEvent("tk:options-state", {
        bubbles: true,
        detail: {
          selections: Object.fromEntries(
            Object.entries(fields).map(([key, field]) => [key, field.values])
          ),
          variantAxes: axes,
          quantity
        }
      })
    );
  }, [config, fields, axes, quantity, props.actionTarget]);
  useEffect3(() => {
    if (!config) return;
    const option = config.options.find((item) => isSizedUploadOptionType2(item.optionType));
    if (!option) return;
    const measured = (files[option.key] ?? []).filter(
      (file) => file.status === "ready" && file.token && typeof file.widthIn === "number" && typeof file.heightIn === "number"
    );
    const first = measured[0];
    const next = {
      values: first && typeof first.widthIn === "number" && typeof first.heightIn === "number" ? sizedSelectionValues({
        widthIn: first.widthIn,
        heightIn: first.heightIn,
        presetId: first.presetId,
        stretched: first.stretched
      }) : [],
      uploadTokens: measured.flatMap((file) => file.token ? [file.token] : [])
    };
    setFields((current) => {
      const previous = current[option.key];
      if (previous && JSON.stringify(previous) === JSON.stringify(next)) return current;
      return { ...current, [option.key]: next };
    });
  }, [config, files]);
  if (loadError) return /* @__PURE__ */ jsx3("p", { class: "error", children: loadError });
  if (!config) return /* @__PURE__ */ jsx3("p", { class: "help", children: "Loading options\u2026" });
  if (!config.blockEnabled) return null;
  const block = parseBlock(config.blockConfig);
  const context = {
    selections: Object.fromEntries(
      Object.entries(fields).map(([key, field]) => [key, field.values])
    ),
    variantAxes: axes,
    quantity
  };
  const options = visibleOptions(config.options, context);
  const sizedOption = options.find((option) => isSizedUploadOptionType2(option.optionType)) ?? null;
  const hideVariantPicker = sizedOption != null && !sizedOptionShowsVariantPicker(sizedOption.config);
  const matches = matchingVariants(config.variants, axes);
  const variant = hideVariantPicker ? findVariantByRef(config.variants, props.variantRef) ?? defaultSizedVariant(sizedOption, config.variants) : config.variantAxes.length > 0 ? matches.length === 1 ? matches[0] : null : matches.length === 1 ? matches[0] : findVariantByRef(config.variants, props.variantRef) ?? null;
  const axesReady = hideVariantPicker || config.variantAxes.every((axis) => !axis.isRequired || axes[axis.optionTypeId]);
  const combinationUnavailable = !hideVariantPicker && config.variantAxes.length > 0 && axesReady && matches.length !== 1;
  const showPrice = props.showPrice === null ? block.showPrice : props.showPrice;
  const priceKnown = variant?.priceCents != null;
  const priceUnavailable = showPrice && axesReady && config.variants.length > 0 && !priceKnown;
  const presentment = props.surface === "STOREFRONT" ? readPresentmentCurrency() : null;
  const converted = presentment != null && props.shopCurrency !== "" && presentment.active !== props.shopCurrency && presentment.rate !== 1;
  const pricingContext = {
    quantity: block.quantity,
    formulaRateCents: variant?.formulaRateCents ?? null,
    minChargeCents: block.minChargeCents,
    presentmentRate: converted && presentment ? presentment.rate : null
  };
  const price = pricingFor(options, fields, variant?.priceCents ?? 0, quantity, pricingContext);
  const sizedItems = [];
  if (sizedOption) {
    for (const file of files[sizedOption.key] ?? []) {
      if (file.status !== "ready" || !file.token || file.widthIn == null || file.heightIn == null) continue;
      sizedItems.push({
        id: file.localId,
        token: file.token,
        widthIn: file.widthIn,
        heightIn: file.heightIn,
        quantity: file.quantity ?? block.quantity.min,
        presetId: file.presetId || "detected",
        stretched: file.stretched === true
      });
    }
  }
  const sizedQuote = sizedOption ? quoteSizedUploads({
    option: sizedOption,
    items: sizedItems,
    options,
    fields,
    variants: config.variants,
    fallbackVariant: variant,
    context: pricingContext
  }) : null;
  const buttonLabel = props.actionLabel.trim() || block.addToCartLabel;
  const setField = (key, next) => {
    setFields((current) => ({ ...current, [key]: next }));
    setAdded(false);
    const option = config?.options.find((item) => item.key === key);
    if (option) {
      setErrors((current) => ({
        ...current,
        [key]: fieldError(option, next, {
          requireFilled: pricesSelectedBlank(option, variant?.blankVariantId)
        })
      }));
    }
  };
  const onAdd = async () => {
    setAdded(false);
    const pending = Object.values(files).some(
      (list) => list.some((file) => file.status === "uploading" || file.status === "processing")
    );
    if (pending) {
      setFormError("Wait until each file is ready.");
      return;
    }
    const nextErrors = {};
    for (const option of options) {
      if (sizedOption && option.key === sizedOption.key) continue;
      nextErrors[option.key] = fieldError(option, fields[option.key], {
        requireFilled: pricesSelectedBlank(option, variant?.blankVariantId)
      });
    }
    if (sizedOption) {
      if (sizedItems.length === 0) {
        nextErrors[sizedOption.key] = fieldError(
          sizedOption,
          { values: [], uploadTokens: [] },
          { requireFilled: pricesSelectedBlank(sizedOption, variant?.blankVariantId) }
        );
      } else {
        for (const item of sizedItems) {
          const itemError = fieldError(
            sizedOption,
            { values: sizedSelectionValues(item), uploadTokens: [item.token] },
            { requireFilled: true }
          );
          if (itemError) {
            nextErrors[sizedOption.key] = itemError;
            break;
          }
          const itemQtyError = quantityError(item.quantity, block.quantity);
          if (itemQtyError) {
            nextErrors[sizedOption.key] = itemQtyError;
            break;
          }
        }
      }
    }
    const pricedHidden = variant?.blankVariantId != null && config.options.some(
      (option) => pricesSelectedBlank(option, variant.blankVariantId) && !options.some((visible) => visible.key === option.key)
    );
    const qtyError = sizedOption ? null : quantityError(quantity, block.quantity);
    setErrors(nextErrors);
    if (qtyError || Object.values(nextErrors).some(Boolean)) {
      setFormError(qtyError);
      return;
    }
    if (sizedQuote && !sizedQuote.ok) {
      setFormError(sizedQuote.error);
      return;
    }
    if (pricedHidden) {
      setFormError("Enter a width and height.");
      return;
    }
    if (combinationUnavailable) {
      setFormError("This combination is unavailable.");
      return;
    }
    if (config.variantAxes.length > 0 && (!axesReady || !variant)) {
      setFormError("Select an available combination.");
      return;
    }
    if (!sizedOption && priceUnavailable) {
      setFormError("Price unavailable");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      if (sizedOption && sizedItems.length > 0 && sizedQuote?.ok) {
        const portalLines = [];
        const tierQuantity = sizedItems.reduce((sum, item) => sum + item.quantity, 0);
        let addedCount = 0;
        for (const item of sizedItems) {
          const file = (files[sizedOption.key] ?? []).find((entry) => entry.localId === item.id);
          const itemFields = {
            ...fields,
            [sizedOption.key]: { values: sizedSelectionValues(item), uploadTokens: [item.token] }
          };
          const quoteLine = sizedQuote.lines.find((line) => line.id === item.id);
          const lineVariant = quoteLine?.variant ?? variant;
          const submission2 = await props.transport.createSubmission(
            {
              schemaVersion: 1,
              uptId: config.uptId,
              variantRef: lineVariant?.externalVariantId || lineVariant?.productVariantId || props.variantRef || null,
              quantity: item.quantity,
              tierQuantity,
              selections: submissionSelections(options, itemFields),
              variantAxes: axisSelections(hideVariantPicker ? lineVariant?.axes ?? axes : axes)
            },
            idempotencyKey()
          );
          const properties2 = [
            ...submission2.lineItemProperties.visible,
            ...submission2.lineItemProperties.hidden,
            ...cartPricingProperties(options, itemFields, "BULK")
          ];
          if (props.surface === "PORTAL") {
            portalLines.push({
              uptId: config.uptId,
              productVariantId: lineVariant?.productVariantId ?? null,
              externalVariantId: lineVariant?.externalVariantId ?? null,
              blankVariantId: lineVariant?.blankVariantId ?? null,
              optionSubmissionId: submission2.token,
              quantity: item.quantity,
              unitPriceCents: quoteLine?.unitCents ?? null,
              summary: submission2.lineItemProperties.visible.map((property) => ({
                label: property.name,
                value: property.value
              })),
              uploads: readyUploadCards(file ? { [sizedOption.key]: [file] } : {})
            });
          } else {
            const cartVariant = lineVariant?.externalVariantId || props.variantRef;
            if (!cartVariant) throw new Error("Select a variant before adding to cart.");
            await addShopifyCartLine({ variantId: cartVariant, quantity: item.quantity, properties: properties2 });
            try {
              await props.transport.markAddedToCart(submission2.token);
            } catch {
            }
          }
          addedCount += 1;
        }
        if (props.surface === "PORTAL") {
          const [first, ...rest] = portalLines;
          if (first) dispatchAddToOrder(props.actionTarget, { ...first, moreLines: rest });
        } else if (addedCount > 0) {
          props.actionTarget.dispatchEvent(
            new CustomEvent("tk:added-to-cart", { bubbles: true, composed: true })
          );
        }
        setAdded(true);
        return;
      }
      const submission = await props.transport.createSubmission(
        {
          schemaVersion: 1,
          uptId: config.uptId,
          variantRef: variant?.externalVariantId || variant?.productVariantId || props.variantRef || null,
          quantity,
          selections: submissionSelections(options, fields),
          variantAxes: axisSelections(hideVariantPicker ? variant?.axes ?? axes : axes)
        },
        idempotencyKey()
      );
      const properties = [
        ...submission.lineItemProperties.visible,
        ...submission.lineItemProperties.hidden,
        ...cartPricingProperties(options, fields, block.quantity.mode)
      ];
      if (props.surface === "PORTAL") {
        dispatchAddToOrder(props.actionTarget, {
          uptId: config.uptId,
          productVariantId: variant?.productVariantId ?? null,
          externalVariantId: variant?.externalVariantId ?? null,
          blankVariantId: variant?.blankVariantId ?? null,
          optionSubmissionId: submission.token,
          quantity,
          unitPriceCents: priceKnown ? price.formulaUnitCents != null || price.discountCents > 0 ? price.customerUnitCents : price.variantUnitPriceCents + price.surchargePerUnitCents : null,
          summary: submission.lineItemProperties.visible.map((property) => ({
            label: property.name,
            value: property.value
          })),
          uploads: readyUploadCards(files)
        });
      } else {
        const cartVariant = variant?.externalVariantId || props.variantRef;
        if (!cartVariant) throw new Error("Select a variant before adding to cart.");
        await addShopifyCartLine({ variantId: cartVariant, quantity, properties });
        try {
          await props.transport.markAddedToCart(submission.token);
        } catch {
        }
        props.actionTarget.dispatchEvent(
          new CustomEvent("tk:added-to-cart", { bubbles: true, composed: true, detail: { token: submission.token } })
        );
      }
      setAdded(true);
    } catch (err) {
      const mapped = mapOptionsError(err, options);
      if (Object.keys(mapped.fields).length > 0) {
        setErrors((current) => ({ ...current, ...mapped.fields }));
      }
      setFormError(mapped.form);
    } finally {
      setBusy(false);
    }
  };
  const duplicateSizedFile = async (optionKey, localId) => {
    const option = options.find((item) => item.key === optionKey);
    const list = files[optionKey] ?? [];
    const source = list.find((file) => file.localId === localId);
    const limits = option ? uploadLimits(option) : null;
    if (!source?.token || source.status !== "ready") return;
    if (limits && list.length >= limits.maxFiles) return;
    const copyId = `${source.localId}-copy-${Date.now().toString(36)}`;
    const pending = {
      ...source,
      localId: copyId,
      token: null,
      status: "processing",
      progress: 90,
      abort: null,
      error: null
    };
    setFiles((current) => {
      const currentList = current[optionKey] ?? [];
      const at = currentList.findIndex((file) => file.localId === localId);
      const insertAt = at < 0 ? currentList.length : at + 1;
      return {
        ...current,
        [optionKey]: [...currentList.slice(0, insertAt), pending, ...currentList.slice(insertAt)]
      };
    });
    setAdded(false);
    try {
      const created = await props.transport.duplicateUpload(source.token);
      setFiles((current) => ({
        ...current,
        [optionKey]: (current[optionKey] ?? []).map(
          (file) => file.localId === copyId ? { ...file, token: created.token, status: "ready", progress: 100, error: null } : file
        )
      }));
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : "Could not duplicate this design.";
      setFiles((current) => ({
        ...current,
        [optionKey]: (current[optionKey] ?? []).map(
          (file) => file.localId === copyId ? { ...file, status: "failed", error: message } : file
        )
      }));
    }
  };
  async function loadRecent(optionKey, query) {
    const requestId = (recentReq.current[optionKey] ?? 0) + 1;
    recentReq.current[optionKey] = requestId;
    try {
      if (!config) return;
      const result = await props.transport.recentUploads(config.uptId, optionKey, {
        q: query.q.trim() || void 0,
        page: query.page,
        limit: query.limit
      });
      if (recentReq.current[optionKey] !== requestId) return;
      setRecent((current) => ({
        ...current,
        [optionKey]: {
          items: result.items,
          page: result.page,
          pageSize: result.pageSize,
          total: result.total,
          q: query.q.trim(),
          loaded: true
        }
      }));
      setRecentError((current) => ({ ...current, [optionKey]: null }));
    } catch (err) {
      if (recentReq.current[optionKey] !== requestId) return;
      setRecentError((current) => ({
        ...current,
        [optionKey]: err instanceof Error ? err.message : "Recent uploads are unavailable."
      }));
    }
  }
  function openRecent(optionKey, tab) {
    setTabs((current) => ({ ...current, [optionKey]: tab }));
    if (tab !== "RECENT") return;
    const view = recentRef.current[optionKey];
    const size = recentLimit.current[optionKey];
    if (view?.loaded || !size) return;
    void loadRecent(optionKey, { q: view?.q ?? "", page: 1, limit: size });
  }
  function searchRecent(optionKey, q) {
    const view = recentRef.current[optionKey];
    void loadRecent(optionKey, { q, page: 1, limit: view?.pageSize || recentLimit.current[optionKey] || 8 });
  }
  function pageRecent(optionKey, page) {
    const view = recentRef.current[optionKey];
    void loadRecent(optionKey, { q: view?.q ?? "", page, limit: view?.pageSize || 8 });
  }
  function measureRecent(optionKey, size) {
    if (recentLimit.current[optionKey] === size) return;
    recentLimit.current[optionKey] = size;
    const view = recentRef.current[optionKey];
    if (view?.loaded && view.pageSize === size) return;
    void loadRecent(optionKey, { q: view?.q ?? "", page: 1, limit: size });
  }
  async function useRecentItem(option, item) {
    const limits = uploadLimits(option);
    const current = files[option.key] ?? [];
    if (current.some((file) => file.token === item.token || file.localId === item.token)) return;
    if (limits && current.length >= limits.maxFiles) return;
    const sized = isSizedUploadOptionType2(option.optionType);
    const draftSized = item.isDraft && item.draft?.widthIn != null && item.draft.heightIn != null;
    if (item.isDraft && (!sized || draftSized)) {
      const card = sized ? fileFromDraft(option, item, block.quantity.min) : readyRecentCard(item, block.quantity.min);
      setFiles((state) => ({ ...state, [option.key]: [...state[option.key] ?? [], card] }));
      setAdded(false);
      if (!sized && card.token) {
        setField(option.key, { values: [], uploadTokens: [...current.flatMap((file) => file.token && file.status === "ready" ? [file.token] : []), card.token] });
      }
      return;
    }
    const localId = `copy-${item.token}`;
    if (current.some((file) => file.localId === localId)) return;
    const pending = {
      localId,
      name: item.fileName || "File",
      progress: 0.5,
      status: "processing",
      token: null,
      error: null,
      abort: null,
      thumbUrl: item.thumbUrl ?? null,
      mimeType: item.mimeType,
      sizeBytes: item.sizeBytes,
      widthPx: item.widthPx ?? null,
      heightPx: item.heightPx ?? null,
      quantity: block.quantity.min
    };
    setFiles((state) => ({ ...state, [option.key]: [...state[option.key] ?? [], pending] }));
    setAdded(false);
    try {
      const copied = await props.transport.duplicateUpload(item.token);
      const status = await props.transport.uploadStatus(copied.token);
      const sizedConfig = sized ? parseSizedConfig(option.config) : null;
      const measured = sizedConfig ? measurementFromStatus(option.optionType, sizedConfig, status) : null;
      const failed = sized && !measured;
      setFiles(
        (state) => updateFile(state, option.key, localId, {
          status: failed ? "failed" : "ready",
          token: copied.token,
          progress: 1,
          error: failed ? "Choose a print size." : null,
          thumbUrl: status.thumbUrl ?? item.thumbUrl ?? null,
          mimeType: status.mimeType ?? item.mimeType,
          sizeBytes: status.sizeBytes ?? item.sizeBytes,
          widthPx: status.widthPx ?? item.widthPx ?? null,
          heightPx: status.heightPx ?? item.heightPx ?? null,
          ...measured ?? {}
        })
      );
      if (!sized) {
        setFields((state) => {
          const existing = state[option.key]?.uploadTokens ?? [];
          if (existing.includes(copied.token)) return state;
          return { ...state, [option.key]: { values: [], uploadTokens: [...existing, copied.token] } };
        });
      }
      if (!failed) {
        scheduleDraftSave(props.transport, {
          token: copied.token,
          status: "ready",
          ...measured ?? {},
          quantity: item.draft?.quantity ?? block.quantity.min
        });
      }
    } catch (err) {
      setFiles(
        (state) => updateFile(state, option.key, localId, {
          status: "failed",
          error: err instanceof Error ? err.message : "Could not copy this file."
        })
      );
    }
  }
  return /* @__PURE__ */ jsxs3("div", { class: "tk", children: [
    config.variantsTruncated ? /* @__PURE__ */ jsx3("p", { class: "banner", children: "Some variants did not load. Refresh the page if a combination is missing." }) : null,
    hideVariantPicker ? null : config.variantAxes.map((axis) => /* @__PURE__ */ jsxs3("div", { class: "field", children: [
      /* @__PURE__ */ jsxs3("span", { class: "legend", children: [
        axis.name,
        axis.isRequired ? /* @__PURE__ */ jsx3("span", { class: "req", children: " *" }) : null
      ] }),
      /* @__PURE__ */ jsx3(
        AxisControl,
        {
          axis,
          value: axes[axis.optionTypeId] ?? "",
          onChange: (value) => {
            setAxes((current) => ({ ...current, [axis.optionTypeId]: value }));
            setAdded(false);
          }
        }
      )
    ] }, axis.optionTypeId)),
    options.map((option) => /* @__PURE__ */ jsx3(
      OptionField,
      {
        option,
        block,
        field: fields[option.key],
        error: errors[option.key] ?? null,
        fileList: files[option.key] ?? [],
        tab: tabs[option.key] ?? "DEVICE",
        recent: recent[option.key] ?? EMPTY_RECENT,
        recentError: recentError[option.key] ?? null,
        sizedQuoteLines: sizedQuote?.ok ? sizedQuote.lines : null,
        formatMoney: (cents) => formatMoney(cents, props.moneyFormat),
        onDuplicate: (localId) => {
          void duplicateSizedFile(option.key, localId);
        },
        onValue: (value) => setField(option.key, { values: value === "" ? [] : [value], uploadTokens: [] }),
        onChecked: (checked) => setField(option.key, { values: checked ? ["true"] : ["false"], uploadTokens: [] }),
        onText: (value) => setField(option.key, { values: value === "" ? [] : [value], uploadTokens: [] }),
        onValues: (values) => setField(option.key, { values, uploadTokens: [] }),
        onTab: (tab) => openRecent(option.key, tab),
        onSearch: (q) => searchRecent(option.key, q),
        onPage: (page) => pageRecent(option.key, page),
        onPageSize: (size) => measureRecent(option.key, size),
        onFiles: (picked) => {
          void acceptFiles({
            option,
            picked,
            existing: files[option.key] ?? [],
            uptId: config.uptId,
            transport: props.transport,
            uploadLimits: config.uploadLimits,
            quantityMin: block.quantity.min,
            setFiles,
            rememberToken: (token) => {
              if (isSizedUploadOptionType2(option.optionType)) return;
              setFields((current) => {
                const existing = current[option.key]?.uploadTokens ?? [];
                if (existing.includes(token)) return current;
                return {
                  ...current,
                  [option.key]: { values: [], uploadTokens: [...existing, token] }
                };
              });
            }
          });
        },
        onReplace: (localId, file) => {
          void acceptFiles({
            option,
            picked: [file],
            existing: files[option.key] ?? [],
            uptId: config.uptId,
            transport: props.transport,
            uploadLimits: config.uploadLimits,
            quantityMin: block.quantity.min,
            replaceLocalId: localId,
            setFiles,
            rememberToken: () => void 0
          });
        },
        onPatch: (localId, patch) => {
          setFiles((current2) => updateFile(current2, option.key, localId, patch));
          setAdded(false);
          if (!isSizedUploadOptionType2(option.optionType)) return;
          const current = (files[option.key] ?? []).find((file) => file.localId === localId);
          if (!current) return;
          scheduleDraftSave(props.transport, { ...current, ...patch });
        },
        onRemove: (localId) => {
          const list = files[option.key] ?? [];
          const target = list.find((file) => file.localId === localId);
          target?.abort?.abort();
          if (target?.token && target.status === "uploading") {
            void props.transport.abortUpload(target.token).catch(() => void 0);
          }
          const next = list.filter((file) => file.localId !== localId);
          if (target?.thumbUrl?.startsWith("blob:") && !next.some((file) => file.thumbUrl === target.thumbUrl)) {
            URL.revokeObjectURL(target.thumbUrl);
          }
          setFiles((current) => ({ ...current, [option.key]: next }));
          if (!isSizedUploadOptionType2(option.optionType)) {
            setField(option.key, {
              values: [],
              uploadTokens: next.flatMap((file) => file.token && file.status === "ready" ? [file.token] : [])
            });
          }
        },
        onUseRecent: (item) => {
          void useRecentItem(option, item);
        }
      },
      option.key
    )),
    sizedOption || block.quantity.mode !== "BULK" ? null : /* @__PURE__ */ jsxs3("div", { class: "field", children: [
      /* @__PURE__ */ jsx3("span", { class: "legend", children: "Quantity tier" }),
      block.quantity.tiers.map((tier) => {
        const tierPrice = pricingFor(
          options,
          fields,
          variant?.priceCents ?? 0,
          tier.minQty,
          pricingContext
        );
        const selected = activeQuantityTier2(block.quantity.tiers, quantity)?.minQty === tier.minQty;
        return /* @__PURE__ */ jsxs3("label", { class: "choice", children: [
          /* @__PURE__ */ jsx3(
            "input",
            {
              type: "radio",
              name: "tk-qty-tier",
              checked: selected,
              onChange: () => setQuantity(tier.minQty)
            }
          ),
          /* @__PURE__ */ jsxs3("span", { children: [
            tier.minQty,
            "+ \xB7 ",
            formatMoney(tierPrice.customerUnitCents, props.moneyFormat),
            " each",
            tier.discountPercent > 0 ? ` \xB7 save ${tier.discountPercent}%` : "",
            " \xB7 ",
            formatMoney(tierPrice.lineTotalCents, props.moneyFormat)
          ] })
        ] }, tier.minQty);
      })
    ] }),
    sizedOption ? null : /* @__PURE__ */ jsxs3("div", { class: "row", children: [
      /* @__PURE__ */ jsx3("span", { class: "legend", children: "Quantity" }),
      /* @__PURE__ */ jsxs3("div", { class: "stepper", children: [
        /* @__PURE__ */ jsx3("button", { type: "button", onClick: () => setQuantity((q) => Math.max(block.quantity.min, q - block.quantity.step)), children: "\u2212" }),
        /* @__PURE__ */ jsx3("span", { children: quantity }),
        /* @__PURE__ */ jsx3("button", { type: "button", onClick: () => setQuantity((q) => Math.min(block.quantity.max, q + block.quantity.step)), children: "+" })
      ] })
    ] }),
    sizedQuote && !sizedQuote.ok ? /* @__PURE__ */ jsx3("p", { class: "error", role: "status", children: sizedQuote.error }) : showPrice && sizedQuote?.ok ? /* @__PURE__ */ jsx3(
      SizedSummary,
      {
        lines: sizedQuote.lines,
        tiers: block.quantity.mode === "BULK" ? block.quantity.tiers : [],
        formatMoney: (cents) => formatMoney(cents, props.moneyFormat),
        estimated: converted
      }
    ) : priceUnavailable ? /* @__PURE__ */ jsx3("p", { class: "error", role: "status", children: "Price unavailable" }) : showPrice && priceKnown && (price.surcharges.length > 0 || price.formulaUnitCents != null || price.discountCents > 0) ? /* @__PURE__ */ jsxs3("div", { children: [
      /* @__PURE__ */ jsxs3("div", { class: "price", children: [
        formatMoney(price.customerUnitCents, props.moneyFormat),
        " each",
        converted ? " estimated" : ""
      ] }),
      price.discountCents > 0 ? /* @__PURE__ */ jsxs3("p", { class: "meta", children: [
        "Save ",
        price.discountPercent,
        "% (",
        formatMoney(price.discountCents, props.moneyFormat),
        ")"
      ] }) : null,
      /* @__PURE__ */ jsxs3("p", { class: "meta", children: [
        "Line total ",
        formatMoney(price.lineTotalCents, props.moneyFormat)
      ] })
    ] }) : showPrice && priceKnown ? /* @__PURE__ */ jsx3("div", { children: /* @__PURE__ */ jsxs3("div", { class: "price", children: [
      formatMoney(price.lineTotalCents, props.moneyFormat),
      converted ? " estimated" : ""
    ] }) }) : null,
    combinationUnavailable ? /* @__PURE__ */ jsx3("p", { class: "error", role: "alert", "aria-live": "polite", children: "This combination is unavailable." }) : formError ? /* @__PURE__ */ jsx3("p", { class: "error", role: "alert", "aria-live": "polite", children: formError }) : null,
    added ? /* @__PURE__ */ jsx3("p", { class: "help", children: props.surface === "PORTAL" ? "Added to the order." : "Added to cart." }) : null,
    /* @__PURE__ */ jsx3(
      "button",
      {
        type: "button",
        class: "atc",
        disabled: busy || combinationUnavailable,
        onClick: () => void onAdd(),
        children: busy ? "Adding\u2026" : buttonLabel
      }
    )
  ] });
}
function OptionField(props) {
  const value = props.field?.values[0] ?? "";
  if (!isUploadOption(props.option.optionType) && props.option.optionType !== "FILE_UPLOAD" && !supportedInput(props.option.optionType) && props.option.optionType !== "INFO") {
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3("p", { class: "banner", children: "This option is not available yet." }) });
  }
  if (props.option.optionType === "INFO") {
    const body = infoBody(props.option.config);
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: null, children: /* @__PURE__ */ jsx3("p", { children: body }) });
  }
  if (props.option.optionType === "CHECKBOX" || props.option.optionType === "BOOLEAN") {
    const label = checkboxLabel(props.option);
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsxs3("label", { children: [
      /* @__PURE__ */ jsx3(
        "input",
        {
          type: "checkbox",
          "aria-label": props.option.displayName,
          checked: value === "true",
          onChange: (event) => props.onChecked(event.currentTarget.checked)
        }
      ),
      " ",
      label || props.option.displayName
    ] }) });
  }
  if (props.option.optionType === "CHECKBOX_GROUP") {
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      CheckboxGroup,
      {
        option: props.option,
        values: props.field?.values ?? [],
        onToggle: (key) => {
          const current = props.field?.values ?? [];
          const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
          props.onValues(next);
        }
      }
    ) });
  }
  if (props.option.optionType === "CUSTOM_SIZE") {
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      DimensionInput,
      {
        option: props.option,
        values: props.field?.values ?? [],
        onChange: props.onValues
      }
    ) });
  }
  if (CHOICE_TYPES.has(props.option.optionType)) {
    const choices = parseChoicesForLabel(props.option);
    const selected = choices.find((choice) => choice.key === value);
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, selected: props.option.optionType === "SWATCH" ? selected?.label : null, children: /* @__PURE__ */ jsx3(ChoiceControl, { option: props.option, value, onChange: props.onValue }) });
  }
  if (isSizedUploadOptionType2(props.option.optionType)) {
    const config = parseSizedConfig(props.option.config);
    if (!config) {
      return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3("p", { class: "banner", children: "This option is misconfigured." }) });
    }
    const limits = uploadLimits(props.option);
    const maxFiles = limits?.maxFiles ?? config.accept.maxFiles ?? 1;
    const atCapacity = props.fileList.length >= maxFiles;
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      SizedUploadPanel,
      {
        optionType: props.option.optionType,
        config,
        block: props.block,
        files: props.fileList,
        accept: uploadAcceptAttribute2(config.accept.mimeTypes),
        atCapacity,
        maxFiles,
        quoteLines: props.sizedQuoteLines,
        formatMoney: props.formatMoney,
        library: uploadLibrary(props),
        onFiles: props.onFiles,
        onReplace: (localId, file) => props.onReplace(localId, file),
        onDuplicate: props.onDuplicate,
        onRemove: props.onRemove,
        onPatch: props.onPatch
      }
    ) });
  }
  if (props.option.optionType === "FILE_UPLOAD") {
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      UploadPanel,
      {
        option: props.option,
        files: props.fileList,
        library: uploadLibrary(props),
        onFiles: props.onFiles,
        onRemove: props.onRemove
      }
    ) });
  }
  if (props.option.optionType === "TEXTAREA" || props.option.optionType === "STRING" || props.option.optionType === "TEXT") {
    const multiline = props.option.optionType === "TEXTAREA";
    const text = parsedText(props.option.config);
    const write = (raw) => props.onText(applyTextTransform(raw, text.textTransform));
    return /* @__PURE__ */ jsxs3(FieldFrame, { option: props.option, error: props.error, children: [
      multiline ? /* @__PURE__ */ jsx3(
        "textarea",
        {
          id: `tk-opt-${props.option.key}`,
          placeholder: props.option.placeholder ?? "",
          value,
          maxLength: text.maxLength,
          "aria-invalid": props.error ? "true" : "false",
          "aria-describedby": props.error ? `tk-opt-${props.option.key}-error` : void 0,
          onInput: (event) => write(event.currentTarget.value)
        }
      ) : /* @__PURE__ */ jsx3(
        "input",
        {
          id: `tk-opt-${props.option.key}`,
          type: "text",
          placeholder: props.option.placeholder ?? "",
          value,
          maxLength: text.maxLength,
          "aria-invalid": props.error ? "true" : "false",
          "aria-describedby": props.error ? `tk-opt-${props.option.key}-error` : void 0,
          onInput: (event) => write(event.currentTarget.value)
        }
      ),
      text.showCounter ? /* @__PURE__ */ jsxs3("p", { class: "counter", children: [
        value.length,
        " / ",
        text.maxLength
      ] }) : null
    ] });
  }
  if (props.option.optionType === "NUMBER") {
    const number = parsedNumber(props.option.config);
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      "input",
      {
        id: `tk-opt-${props.option.key}`,
        type: "number",
        inputMode: "decimal",
        value,
        min: number.min ?? void 0,
        max: number.max ?? void 0,
        step: number.step,
        "aria-invalid": props.error ? "true" : "false",
        "aria-describedby": props.error ? `tk-opt-${props.option.key}-error` : void 0,
        onInput: (event) => props.onText(event.currentTarget.value)
      }
    ) });
  }
  if (props.option.optionType === "DATE") {
    const date = parsedDate(props.option.config);
    return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3(
      "input",
      {
        id: `tk-opt-${props.option.key}`,
        type: "date",
        value,
        min: date.minDate ?? void 0,
        max: date.maxDate ?? void 0,
        "aria-invalid": props.error ? "true" : "false",
        "aria-describedby": props.error ? `tk-opt-${props.option.key}-error` : void 0,
        onInput: (event) => props.onText(event.currentTarget.value)
      }
    ) });
  }
  return /* @__PURE__ */ jsx3(FieldFrame, { option: props.option, error: props.error, children: /* @__PURE__ */ jsx3("p", { class: "banner", children: "This option is not available yet." }) });
}
function supportedInput(optionType) {
  return CHOICE_TYPES.has(optionType) || optionType === "TEXT" || optionType === "TEXTAREA" || optionType === "STRING" || optionType === "NUMBER" || optionType === "CHECKBOX" || optionType === "CHECKBOX_GROUP" || optionType === "BOOLEAN" || optionType === "DATE" || optionType === "CUSTOM_SIZE" || optionType === "FILE_UPLOAD" || optionType === "INFO";
}
function checkboxLabel(option) {
  const parsed = checkboxConfigSchema.safeParse(option.config ?? {});
  return parsed.success ? parsed.data.label ?? "" : "";
}
function parsedText(config) {
  const parsed = textConfigSchema.safeParse(config ?? {});
  return parsed.success ? parsed.data : { minLength: 0, maxLength: 500, showCounter: false, textTransform: "NONE" };
}
function parsedNumber(config) {
  const parsed = numberConfigSchema.safeParse(config ?? {});
  return parsed.success ? parsed.data : { min: null, max: null, step: 1 };
}
function parsedDate(config) {
  const parsed = dateConfigSchema.safeParse(config ?? {});
  return parsed.success ? parsed.data : { minDate: null, maxDate: null, blackoutDates: [] };
}
function parseChoicesForLabel(option) {
  if (!Array.isArray(option.values)) return [];
  return option.values.flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const record = row;
    if (typeof record.key !== "string" || typeof record.label !== "string") return [];
    return [{ key: record.key, label: record.label }];
  });
}
function infoBody(config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) return "";
  const body = config.body;
  return typeof body === "string" ? body : "";
}
function uploadSources(option, block) {
  const limits = uploadLimits(option);
  const optionSources = limits ? ["DEVICE", "RECENT"] : ["DEVICE"];
  const parsed = option.config;
  const fromOption = parsed && typeof parsed === "object" && !Array.isArray(parsed) && Array.isArray(parsed.sources) ? parsed.sources.filter((item) => typeof item === "string") : optionSources;
  const allowed = new Set(block.uploadPanel.sources);
  const intersection = fromOption.filter(
    (source) => allowed.has(source) && (source === "DEVICE" || source === "RECENT")
  );
  return intersection.length > 0 ? intersection : ["DEVICE"];
}
async function acceptFiles(input) {
  const limits = uploadLimits(input.option);
  if (!limits || !input.uptId) return;
  const sized = isSizedUploadOptionType2(input.option.optionType);
  const replacing = input.replaceLocalId ? input.existing.find((file) => file.localId === input.replaceLocalId) : void 0;
  const room = limits.maxFiles - input.existing.length + (replacing ? 1 : 0);
  const accepted = input.picked.slice(0, replacing ? 1 : Math.max(0, room));
  for (const file of accepted) {
    const rejected = file.size > limits.maxFileMb * 1024 * 1024 ? `Files must be ${limits.maxFileMb} MB or smaller.` : limits.mimeTypes.length > 0 && !isMimeAllowed(canonicalDeclaredMime2(file.type, file.name), limits.mimeTypes) ? "This file type is not accepted." : null;
    if (rejected) {
      input.setFiles((current) => ({
        ...current,
        [input.option.key]: [
          ...current[input.option.key] ?? [],
          {
            localId: `${file.name}-${file.size}-rejected`,
            name: file.name,
            progress: 0,
            status: "failed",
            token: null,
            error: rejected,
            abort: null
          }
        ]
      }));
      continue;
    }
    const localId = `${file.name}-${file.lastModified}-${file.size}`;
    const abort = new AbortController();
    const sizedConfig = sized ? parseSizedConfig(input.option.config) : null;
    const tracked = {
      localId,
      name: file.name,
      progress: 0,
      status: "uploading",
      token: null,
      error: null,
      abort,
      thumbUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      mimeType: file.type || null,
      sizeBytes: file.size,
      quantity: replacing?.quantity ?? input.quantityMin
    };
    input.setFiles((current) => ({ ...current, [input.option.key]: [...current[input.option.key] ?? [], tracked] }));
    try {
      const uploaded = await uploadOptionFile({
        transport: input.transport,
        uptId: input.uptId,
        optionKey: input.option.key,
        file,
        signal: abort.signal,
        onProgress: (progress) => {
          const ratio = progress.total > 0 ? progress.loaded / progress.total : 0;
          input.setFiles((current) => {
            const previous = (current[input.option.key] ?? []).find((item) => item.localId === localId)?.progress ?? 0;
            const next = sized ? uploadProgressPercent("uploading", ratio) / 100 : ratio;
            return updateFile(current, input.option.key, localId, {
              progress: monotonicProgress(previous, next)
            });
          });
        }
      });
      input.setFiles((current) => updateFile(current, input.option.key, localId, {
        status: "processing",
        token: uploaded.token,
        progress: sized ? uploadProgressPercent("detecting", 1) / 100 : 1
      }));
      const ready = await pollUploadReady(input.transport, uploaded.token, {
        signal: abort.signal,
        limits: {
          maxPixels: sized ? input.uploadLimits?.sizedMaxPixels ?? input.uploadLimits?.maxPixels : input.uploadLimits?.maxPixels,
          maxFileMb: input.uploadLimits?.maxFileMbCeiling
        }
      });
      const measured = sized && sizedConfig ? measurementFromStatus(input.option.optionType, sizedConfig, ready) : null;
      input.setFiles((current) => {
        const existing = (current[input.option.key] ?? []).find((item) => item.localId === localId);
        if (ready.thumbUrl && existing?.thumbUrl?.startsWith("blob:")) URL.revokeObjectURL(existing.thumbUrl);
        const keptQty = replacing?.quantity ?? existing?.quantity ?? input.quantityMin;
        let next = updateFile(current, input.option.key, localId, {
          status: sized && !measured ? "failed" : "ready",
          token: ready.token,
          progress: 1,
          error: sized && !measured ? "The image could not be measured." : null,
          ...ready.thumbUrl ? { thumbUrl: ready.thumbUrl } : {},
          mimeType: ready.mimeType ?? file.type ?? null,
          sizeBytes: ready.sizeBytes ?? file.size,
          widthPx: ready.widthPx ?? null,
          heightPx: ready.heightPx ?? null,
          quantity: keptQty,
          ...measured ?? {}
        });
        const saved = (next[input.option.key] ?? []).find((item) => item.localId === localId);
        if (saved?.status === "ready") scheduleDraftSave(input.transport, saved);
        if (replacing && (!sized || measured)) {
          const old = (next[input.option.key] ?? []).find((item) => item.localId === replacing.localId);
          if (old?.thumbUrl?.startsWith("blob:")) URL.revokeObjectURL(old.thumbUrl);
          next = {
            ...next,
            [input.option.key]: (next[input.option.key] ?? []).filter((item) => item.localId !== replacing.localId)
          };
        }
        return next;
      });
      if (!sized || measured) input.rememberToken(ready.token);
    } catch (err) {
      if (abort.signal.aborted) return;
      input.setFiles((current) => updateFile(current, input.option.key, localId, {
        status: "failed",
        error: err instanceof Error ? err.message : "Upload failed."
      }));
    }
  }
}
function readyUploadCards(files) {
  const cards = [];
  for (const list of Object.values(files)) {
    for (const file of list) {
      if (file.status !== "ready" || !file.token) continue;
      cards.push({
        fileName: file.name,
        thumbUrl: file.thumbUrl && !file.thumbUrl.startsWith("blob:") ? file.thumbUrl : null,
        mimeType: file.mimeType ?? null,
        sizeBytes: typeof file.sizeBytes === "number" ? file.sizeBytes : null,
        widthPx: typeof file.widthPx === "number" ? file.widthPx : null,
        heightPx: typeof file.heightPx === "number" ? file.heightPx : null
      });
    }
  }
  return cards;
}
function readyRecentCard(item, quantityMin) {
  return {
    localId: item.token,
    name: item.fileName || "File",
    progress: 1,
    status: "ready",
    token: item.token,
    error: null,
    abort: null,
    thumbUrl: item.thumbUrl ?? null,
    mimeType: item.mimeType,
    sizeBytes: item.sizeBytes,
    widthPx: item.widthPx ?? null,
    heightPx: item.heightPx ?? null,
    quantity: item.draft?.quantity || quantityMin
  };
}
function uploadLibrary(props) {
  return {
    tab: props.tab === "RECENT" ? "RECENT" : "DEVICE",
    sources: uploadSources(props.option, props.block),
    items: props.recent.items,
    error: props.recentError,
    query: props.recent.q,
    page: props.recent.page,
    pageSize: props.recent.pageSize,
    total: props.recent.total,
    loaded: props.recent.loaded,
    onTab: (tab) => props.onTab(tab),
    onSearch: props.onSearch,
    onPage: props.onPage,
    onPageSize: props.onPageSize,
    onUse: props.onUseRecent
  };
}
function fileFromDraft(option, item, quantityMin) {
  const draft = item.draft;
  const config = option.optionType === "GANGSHEET_UPLOAD_SIZED" ? parseSizedConfig(option.config) : null;
  const measured = config ? measurementFromStatus(option.optionType, config, {
    token: item.token,
    status: "READY",
    scanStatus: "CLEAN",
    trimmedWidthPx: item.trimmedWidthPx ?? null,
    trimmedHeightPx: item.trimmedHeightPx ?? null,
    effectiveDpi: item.effectiveDpi ?? null
  }) : null;
  return {
    localId: item.token,
    name: item.fileName || "File",
    progress: 1,
    status: "ready",
    token: item.token,
    error: null,
    abort: null,
    thumbUrl: item.previewUrl ?? item.thumbUrl ?? null,
    mimeType: item.mimeType,
    sizeBytes: item.sizeBytes,
    widthPx: item.widthPx ?? null,
    heightPx: item.heightPx ?? null,
    trimmedWidthPx: item.trimmedWidthPx ?? null,
    trimmedHeightPx: item.trimmedHeightPx ?? null,
    nativeWidthIn: measured?.nativeWidthIn ?? item.nativeWidthIn ?? null,
    nativeHeightIn: measured?.nativeHeightIn ?? item.nativeHeightIn ?? null,
    effectiveDpi: measured?.effectiveDpi ?? item.effectiveDpi ?? null,
    widthIn: measured?.widthIn ?? draft?.widthIn ?? null,
    heightIn: measured?.heightIn ?? draft?.heightIn ?? null,
    presetId: measured?.presetId ?? draft?.presetId ?? "custom",
    ratioLocked: draft?.ratioLocked !== false,
    stretched: false,
    quantity: draft?.quantity || quantityMin,
    previewBg: draft?.bgColorHex ?? void 0
  };
}
function updateFile(current, optionKey, localId, patch) {
  return {
    ...current,
    [optionKey]: (current[optionKey] ?? []).map((file) => file.localId === localId ? { ...file, ...patch } : file)
  };
}

// src/transport.ts
function recentUrl(path, uptId, optionKey, query) {
  const params = new URLSearchParams({ uptId, optionKey });
  const q = query?.q?.trim();
  if (q) params.set("q", q.slice(0, 80));
  if (query?.page && query.page > 0) params.set("page", String(query.page));
  if (query?.limit && query.limit > 0) params.set("limit", String(query.limit));
  return `${path}?${params.toString()}`;
}
async function readError(response) {
  try {
    const body = await response.json();
    const rawCode = typeof body.error === "string" ? body.error : body.code;
    const code = typeof rawCode === "string" && rawCode.trim() !== "" ? rawCode : null;
    if (typeof body.message === "string" && body.message.trim() !== "") {
      return new OptionsRequestError(body.message, code);
    }
    if (Array.isArray(body.message)) {
      const text = body.message.filter((item) => typeof item === "string").join(" ");
      if (text) return new OptionsRequestError(text, code);
    }
    return new OptionsRequestError(`Request failed (${response.status}).`, code);
  } catch {
    return new OptionsRequestError(`Request failed (${response.status}).`, null);
  }
}
async function sendJson(url, init) {
  const headers = {
    Accept: "application/json",
    ...init.headers
  };
  if (init.body !== void 0) headers["Content-Type"] = "application/json";
  const response = await fetch(url, {
    method: init.method,
    headers,
    body: init.body !== void 0 ? JSON.stringify(init.body) : void 0
  });
  if (!response.ok) throw await readError(response);
  return await response.json();
}
function storefrontHeaders(idempotencyKey2) {
  const headers = {
    "X-Designer-Session": getDesignerSessionId()
  };
  const customer = getCustomerToken();
  if (customer) headers["X-TK-Customer-Token"] = customer;
  if (idempotencyKey2) headers["Idempotency-Key"] = idempotencyKey2;
  return headers;
}
function createShopifyProxyTransport(basePath = "/apps/tk-pod/options") {
  const base = basePath.replace(/\/$/, "");
  return {
    getConfig: ({ productRef }) => sendJson(
      `${base}/config?productRef=${encodeURIComponent(productRef ?? "")}`,
      { method: "GET", headers: storefrontHeaders() }
    ),
    createSubmission: (body, idempotencyKey2) => sendJson(`${base}/submissions`, {
      method: "POST",
      body,
      headers: storefrontHeaders(idempotencyKey2)
    }),
    markAddedToCart: (token) => sendJson(`${base}/submissions/${encodeURIComponent(token)}/added-to-cart`, {
      method: "POST",
      body: {},
      headers: storefrontHeaders()
    }),
    initUpload: (body, idempotencyKey2) => sendJson(`${base}/uploads`, {
      method: "POST",
      body,
      headers: storefrontHeaders(idempotencyKey2)
    }),
    signParts: (token, partNumbers) => sendJson(`${base}/uploads/${encodeURIComponent(token)}/parts`, {
      method: "POST",
      body: { partNumbers },
      headers: storefrontHeaders()
    }),
    completeUpload: (token, parts) => sendJson(`${base}/uploads/${encodeURIComponent(token)}/complete`, {
      method: "POST",
      body: { parts },
      headers: storefrontHeaders()
    }),
    abortUpload: async (token) => {
      await sendJson(`${base}/uploads/${encodeURIComponent(token)}/abort`, {
        method: "POST",
        body: {},
        headers: storefrontHeaders()
      });
    },
    uploadStatus: (token) => sendJson(`${base}/uploads/${encodeURIComponent(token)}`, {
      method: "GET",
      headers: storefrontHeaders()
    }),
    recentUploads: (uptId, optionKey, query) => sendJson(recentUrl(`${base}/uploads/recent`, uptId, optionKey, query), {
      method: "GET",
      headers: storefrontHeaders()
    }),
    duplicateUpload: (token) => sendJson(`${base}/uploads/${encodeURIComponent(token)}/duplicate`, {
      method: "POST",
      body: {},
      headers: storefrontHeaders()
    }),
    saveDraft: (token, draft) => sendJson(`${base}/uploads/${encodeURIComponent(token)}/draft`, {
      method: "POST",
      body: draft,
      headers: storefrontHeaders()
    })
  };
}
function createPortalTransport(request) {
  const call = (path, init) => request(path, init);
  return {
    getConfig: ({ uptId }) => call(`/api/v1/catalog/templates/${encodeURIComponent(uptId ?? "")}/options-config`, {
      method: "GET"
    }),
    createSubmission: (body, idempotencyKey2) => call("/api/v1/option-submissions", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": idempotencyKey2 }
    }),
    markAddedToCart: (token) => call(`/api/v1/option-submissions/${encodeURIComponent(token)}/added-to-cart`, {
      method: "POST",
      body: {}
    }),
    initUpload: (body, idempotencyKey2) => call("/api/v1/option-uploads", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": idempotencyKey2 }
    }),
    signParts: (token, partNumbers) => call(`/api/v1/option-uploads/${encodeURIComponent(token)}/parts`, {
      method: "POST",
      body: { partNumbers }
    }),
    completeUpload: (token, parts) => call(`/api/v1/option-uploads/${encodeURIComponent(token)}/complete`, {
      method: "POST",
      body: { parts }
    }),
    abortUpload: async (token) => {
      await call(`/api/v1/option-uploads/${encodeURIComponent(token)}/abort`, {
        method: "POST",
        body: {}
      });
    },
    uploadStatus: (token) => call(`/api/v1/option-uploads/${encodeURIComponent(token)}`, { method: "GET" }),
    recentUploads: (uptId, optionKey, query) => call(recentUrl("/api/v1/option-uploads/recent", uptId, optionKey, query), {
      method: "GET"
    }),
    duplicateUpload: (token) => call(`/api/v1/option-uploads/${encodeURIComponent(token)}/duplicate`, {
      method: "POST",
      body: {}
    }),
    saveDraft: (token, draft) => call(`/api/v1/option-uploads/${encodeURIComponent(token)}/draft`, {
      method: "POST",
      body: draft
    })
  };
}

// src/styles.ts
var WIDGET_CSS = `:host{display:block;color:#111827;font:14px/1.4 system-ui,sans-serif;--g:repeating-conic-gradient(#eee 0 25%,#fff 0 50%)0/16px 16px;}*{box-sizing:border-box;}.tk{display:grid;gap:16px;}.field{display:grid;gap:6px;}.field label,.legend{font-weight:600;}.req{color:#b45309;}.help,.meta{color:#6b7280;font-size:12px;}.error{color:#b91c1c;font-size:13px;}input[type="text"],input[type="number"],input[type="date"],textarea,select{width:100%;border:1px solid #d1d5db;border-radius:8px;padding:8px 10px;font:inherit;background:#fff;}textarea{min-height:88px;resize:vertical;}.choices,.axes{display:flex;flex-wrap:wrap;gap:8px;}button{font:inherit}.choice,.swatch,.axis{border:1px solid #d1d5db;background:#fff;border-radius:8px;padding:8px 12px;cursor:pointer;}.choice,.axis{min-height:40px;display:inline-flex;align-items:center;gap:6px;}.choice:has(input:disabled){opacity:0.55;cursor:not-allowed;}:focus-visible{outline:2px solid var(--tk-accent,#111827);outline-offset:2px;}:is(.choice,.swatch,.axis)[aria-pressed="true"]{border-color:var(--tk-accent,#111827);box-shadow:inset 0 0 0 1px var(--tk-accent,#111827);}.swatch{width:var(--tk-swatch,40px);height:var(--tk-swatch,40px);padding:0;border-radius:999px;overflow:hidden;position:relative;}.swatch img{width:100%;height:100%;object-fit:cover;display:block;}.swatch-mark{position:absolute;inset:0;display:grid;place-items:center;color:#fff;font-size:14px;text-shadow:0 1px 2px #111;}.card{display:grid;gap:4px;width:96px;min-height:96px;font-size:12px;justify-items:center;align-content:center;}.card img{width:100%;height:72px;object-fit:cover;border-radius:6px;}.counter{color:#6b7280;font-size:12px;text-align:right;}.dims{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:8px;}.dim{display:grid;gap:4px;font-weight:600;}.dim-input{display:flex;align-items:center;gap:6px;}.dim-input input{width:100%;}.choice.card input,.drop input[type="file"],.s-ib input[type="file"]{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);}.choice.card{position:relative;}.choice.card:has(input:checked){border-color:var(--tk-accent,#111827);box-shadow:inset 0 0 0 1px var(--tk-accent,#111827);}.drop{position:relative;display:flex;flex-direction:column;align-items:center;min-height:96px;border:1px dashed #9ca3af;border-radius:10px;padding:16px;background:#f9fafb;cursor:pointer;}.drop.over{border-color:var(--tk-accent,#111827);background:#f3f4f6;}.drop-icon{color:#6b7280;}.drop-title{font-weight:600;}.drop-action{color:var(--tk-accent,#111827);font-size:13px;text-decoration:underline;}.tabs{display:flex;gap:8px;}.tab{border:0;background:transparent;font:inherit;padding:4px 0;cursor:pointer;color:#6b7280;}.tab[aria-selected="true"]{color:#111827;font-weight:600;box-shadow:inset 0 -2px 0 var(--tk-accent,#111827);}.file{display:flex;justify-content:space-between;gap:12px;align-items:center;padding:8px 0;border-top:1px solid #e5e7eb;}.file-thumb{width:72px;height:72px;object-fit:contain;border-radius:6px;border:1px solid #e5e7eb;flex:none;background:var(--g);}.file-icon{width:48px;height:48px;flex:none;display:grid;place-items:center;border-radius:6px;background:#f3f4f6;}.lib{display:grid;gap:8px;padding:8px 12px;border:1px dashed #9ca3af;border-radius:10px;background:#f9fafb;}.recent-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:8px;}.recent-card{display:grid;gap:4px;padding:6px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;text-align:left;cursor:pointer;}.recent-card img{width:100%;height:72px;object-fit:contain;border-radius:4px;background:var(--g);}.chip{display:inline-block;font-size:12px;font-weight:600;padding:2px 8px;border-radius:999px;background:#f3f4f6;color:#374151;}.chip.ready,.chip.ok,.chip.dpi.good{background:#ecfdf5;color:#047857;}.chip.failed,.chip.dpi.low{background:#fef2f2;color:#b91c1c;}.recent-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;}.file-body{flex:1;min-width:0;display:grid;gap:4px;}.file-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;}.bar{height:8px;background:#e5e7eb;border-radius:999px;overflow:hidden;}.bar span{display:block;height:100%;background:var(--tk-accent,#111827);transition:width 0.2s linear;}.bar.indeterminate span{width:35%;animation:tk-upload 1s ease-in-out infinite;}@keyframes tk-upload{0%{margin-left:0;}50%{margin-left:65%;}100%{margin-left:0;}}.ready{color:#047857;font-size:12px;}.row{display:flex;justify-content:space-between;gap:12px;align-items:center;}.stepper{display:flex;align-items:center;gap:8px;}.stepper button{width:32px;height:32px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer;}.price{font-size:18px;font-weight:700;}.atc{width:100%;border:0;border-radius:8px;padding:12px 16px;font:inherit;font-weight:600;color:var(--tk-accent-text,#fff);background:var(--tk-accent,#111827);cursor:pointer;}.atc:disabled{opacity:0.55;cursor:not-allowed;}.banner{padding:10px 12px;border-radius:8px;background:#fff7ed;color:#9a3412;}button.link{border:0;background:transparent;color:#b91c1c;cursor:pointer;font:inherit;}.sized{display:grid;gap:12px;}.drop.compact{min-height:64px;padding:10px 16px;}.s-card{display:grid;gap:10px;padding:10px 12px 12px;border:1px solid #e5e7eb;border-radius:12px;background:#fff;}.s-card.busy{opacity:0.85;}.s-card.failed{border-color:#fecaca;}.s-head{display:flex;align-items:center;gap:8px;min-width:0;}.s-num{flex:none;width:22px;height:22px;border-radius:999px;display:grid;place-items:center;background:#111827;color:#fff;font-size:12px;font-weight:700;}.s-name{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;}.s-ib{position:relative;flex:none;width:32px;height:32px;display:grid;place-items:center;border-radius:8px;border:1px solid #e5e7eb;background:#fff;color:#374151;cursor:pointer;}.s-ib:hover:not(:disabled){background:#f3f4f6;}.s-ib:disabled{opacity:0.45;cursor:not-allowed;}.s-ib.del{color:#b91c1c;}.s-body{display:grid;gap:12px;}.s-prevcol{display:grid;gap:4px;}.s-preview{position:relative;display:grid;place-items:center;min-height:150px;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;}.s-preview.checker,.s-sw.checker{background:var(--g);}.s-preview img{width:100%;height:150px;object-fit:contain;display:block;}.s-skel{display:grid;gap:8px;justify-items:center;color:#6b7280;font-size:12px;}.s-skel .bar{width:100%;}.s-bgbar{position:absolute;left:6px;bottom:6px;display:flex;gap:4px;padding:4px;border-radius:999px;background:#fffe;box-shadow:0 1px 2px #0003;}.s-sw{position:relative;width:18px;height:18px;border-radius:999px;border:1px solid #d1d5db;padding:0;cursor:pointer;overflow:hidden;background:#fff;}.s-sw.on{box-shadow:0 0 0 2px #fff,0 0 0 4px var(--tk-accent,#111827);}.s-sw.custom{background:conic-gradient(#f87171,#fbbf24,#34d399,#60a5fa,#a78bfa,#f87171);}.s-sw.custom input{position:absolute;inset:0;opacity:0;width:100%;height:100%;cursor:pointer;}.s-prevsize{text-align:center;color:#6b7280;font-size:12px;min-height:16px;}.s-ctrl{display:grid;gap:10px;align-content:start;}.s-dims{display:grid;grid-template-columns:1fr auto 1fr;align-items:end;gap:8px;}.s-field{display:grid;gap:4px;min-width:0;}.s-field > span{font-size:12px;font-weight:600;color:#374151;}.s-field > span small{font-weight:400;color:#6b7280;}.s-field select{width:100%;}.s-row2{display:grid;grid-template-columns:1fr auto;gap:8px;align-items:end;}.s-step{display:flex;align-items:stretch;border:1px solid #d1d5db;border-radius:8px;overflow:hidden;background:#fff;}.s-step button{width:34px;border:0;background:#f9fafb;color:#111827;font:inherit;font-size:16px;cursor:pointer;flex:none;}.s-step button:hover:not(:disabled){background:#f3f4f6;}.s-step button:disabled{opacity:0.4;cursor:not-allowed;}.s-step input[type="text"]{width:100%;min-width:56px;border:0;border-radius:0;text-align:center;padding:8px 4px;font:inherit;-moz-appearance:textfield;}.s-row2 .s-step input[type="text"]{width:48px;}.s-step input:focus-visible{outline:none;box-shadow:inset 0 0 0 2px var(--tk-accent,#111827);}.s-lock{position:relative;width:36px;height:38px;display:grid;place-items:center;border-radius:8px;border:1px solid #d1d5db;background:#f3f4f6;cursor:pointer;flex:none;}.s-lock.open{background:#fff7ed;border-color:#fdba74;color:#9a3412;}.s-lock:disabled{opacity:0.5;cursor:not-allowed;}.s-lock[data-tip]:hover::after{content:attr(data-tip);position:absolute;bottom:100%;left:0;width:160px;padding:4px;background:#111;color:#fff;font-size:11px;z-index:2;}.s-info{display:grid;gap:8px;}.s-chips{display:flex;flex-wrap:wrap;gap:6px;}.chip.warn,.chip.dpi.fair{background:#fff7ed;color:#9a3412;}.s-price{display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;}.s-price-l{font-weight:700;font-size:16px;}.s-price-l span{font-weight:400;font-size:12px;color:#6b7280;}.s-price-l s{margin-left:6px;font-weight:400;font-size:13px;color:#9ca3af;}.s-price-r{color:#374151;}.s-err{margin:0;}.s-summary{display:grid;gap:6px;padding:12px;border:1px solid #e5e7eb;border-radius:12px;background:#f9fafb;}.s-sum-title{margin:0 0 2px;font-weight:700;}.s-sum-row{display:flex;justify-content:space-between;gap:12px;font-size:13px;color:#374151;}.s-sum-row.disc{color:#047857;}.s-sum-total{display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding-top:6px;border-top:1px solid #e5e7eb;font-size:18px;}.s-sum-total span{display:grid;}.s-sum-total small{font-size:11px;color:#6b7280;font-weight:400;}.s-nudge{padding:8px 10px;border-radius:8px;background:#eef2ff;color:#3730a3;font-size:13px;}.s-nudge-bar{height:6px;margin-top:6px;border-radius:999px;background:#c7d2fe;overflow:hidden;}.s-nudge-bar i{display:block;height:100%;background:#4f46e5;}.s-tiers summary{cursor:pointer;font-size:13px;color:var(--tk-accent,#111827);}.s-tiers table{width:100%;margin-top:6px;border-collapse:collapse;font-size:13px;}.s-tiers td{padding:4px 0;border-top:1px solid #e5e7eb;}.s-tiers td:last-child{text-align:right;}.s-tiers tr.on td{font-weight:700;}@media (min-width:720px){.s-body{grid-template-columns:180px 1fr;}.s-info{grid-column:1 / -1;}}`;

// src/element.tsx
import { jsx as jsx4 } from "preact/jsx-runtime";
var TAG = "tk-product-options";
var HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/;
function optionalBool(value) {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}
var TkProductOptionsElement = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.mountPoint = null;
    this.injected = null;
    this.fallback = createShopifyProxyTransport();
  }
  static get observedAttributes() {
    return [
      "surface",
      "product-ref",
      "upt-id",
      "variant-id",
      "money-format",
      "accent-color",
      "accent-text",
      "show-price",
      "action-label"
    ];
  }
  connectedCallback() {
    if (this.shadowRoot) return;
    const root = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = WIDGET_CSS;
    this.mountPoint = document.createElement("div");
    root.append(style, this.mountPoint);
    this.style.display = "block";
    this.paint();
  }
  attributeChangedCallback() {
    this.paint();
  }
  disconnectedCallback() {
    if (this.mountPoint) render(null, this.mountPoint);
  }
  setTransport(transport) {
    this.injected = transport;
    this.paint();
  }
  paint() {
    if (!this.mountPoint) return;
    const accent = this.getAttribute("accent-color");
    if (accent && HEX_COLOR.test(accent)) {
      this.style.setProperty("--tk-accent", accent);
    }
    const accentText = this.getAttribute("accent-text");
    if (accentText && HEX_COLOR.test(accentText)) {
      this.style.setProperty("--tk-accent-text", accentText);
    }
    const surface = this.getAttribute("surface") === "portal" ? "PORTAL" : "STOREFRONT";
    render(
      /* @__PURE__ */ jsx4(
        OptionsApp,
        {
          surface,
          productRef: this.getAttribute("product-ref") ?? "",
          uptId: this.getAttribute("upt-id") ?? "",
          variantRef: this.getAttribute("variant-id") ?? "",
          moneyFormat: this.getAttribute("money-format") || "${{amount}}",
          shopCurrency: this.getAttribute("shop-currency") ?? "",
          showPrice: optionalBool(this.getAttribute("show-price")),
          actionLabel: this.getAttribute("action-label") ?? "",
          transport: this.injected ?? this.fallback,
          actionTarget: this
        }
      ),
      this.mountPoint
    );
  }
};
function defineTkProductOptions() {
  if (typeof customElements === "undefined") return;
  if (!customElements.get(TAG)) customElements.define(TAG, TkProductOptionsElement);
}

// src/preview.ts
import { h, render as render2 } from "preact";
function previewOnly() {
  return Promise.reject(new Error("This is a preview. Save the option to use it on the product."));
}
function previewTransport(config) {
  return {
    getConfig: async () => config,
    createSubmission: previewOnly,
    markAddedToCart: previewOnly,
    initUpload: previewOnly,
    signParts: previewOnly,
    completeUpload: previewOnly,
    abortUpload: async () => void 0,
    uploadStatus: previewOnly,
    recentUploads: async () => ({ items: [], page: 1, pageSize: 8, total: 0 }),
    duplicateUpload: previewOnly,
    saveDraft: previewOnly
  };
}
function mountOptionsPreview(host, config) {
  const root = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  if (!root.querySelector("style")) {
    const style = document.createElement("style");
    style.textContent = WIDGET_CSS;
    root.appendChild(style);
  }
  let mount = root.querySelector("[data-tk-preview]");
  if (!mount) {
    mount = document.createElement("div");
    mount.setAttribute("data-tk-preview", "");
    root.appendChild(mount);
  }
  render2(
    h(OptionsApp, {
      surface: "PORTAL",
      productRef: "",
      uptId: config.uptId,
      variantRef: "",
      moneyFormat: "${{amount}}",
      shopCurrency: "",
      showPrice: true,
      actionLabel: "Preview only",
      transport: previewTransport(config),
      actionTarget: host
    }),
    mount
  );
  return () => {
    render2(null, mount);
  };
}

// src/index.ts
defineTkProductOptions();
export {
  ADD_TO_ORDER_EVENT,
  TkProductOptionsElement,
  createPortalTransport,
  createShopifyProxyTransport,
  defineTkProductOptions,
  mountOptionsPreview
};
//# sourceMappingURL=index.js.map